import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, Check, ChevronRight, ChevronDown, Plus, Trash2, Edit2, GripVertical, ArrowRight, Zap } from 'lucide-react';

/**
 * FlowchartTab - Visual flowchart representation of the conversation flow
 * Uses proper flowchart symbols: ovals (start/end), rectangles (process), diamonds (decisions)
 */
function FlowchartTab({ config }) {
    const [selectedPhase, setSelectedPhase] = useState('all');

    if (!config) return null;

    const phases = config.phases || {};
    const stepSequencing = config.stepSequencing || {};
    const phaseOrder = stepSequencing.phaseOrder || [];
    const stepFlows = stepSequencing.stepFlows || {};
    const conditionalPhases = stepSequencing.conditionalPhases || {};
    const conditionalSteps = stepSequencing.conditionalSteps || {};

    // Flowchart symbol styles
    const styles = {
        terminal: {
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            borderRadius: '50px',
            padding: 'var(--space-2) var(--space-4)',
            color: 'white',
            fontWeight: 'bold',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
            fontSize: 'var(--font-size-sm)',
        },
        process: {
            background: 'var(--color-surface)',
            border: '2px solid var(--color-primary)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-2) var(--space-3)',
            textAlign: 'center',
            minWidth: '160px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        decision: {
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            color: 'white',
            padding: 'var(--space-1) var(--space-2)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'bold',
        },
        io: {
            background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-2) var(--space-3)',
            color: 'white',
            textAlign: 'center',
            transform: 'skewX(-5deg)',
            boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)',
        },
        phaseHeader: {
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3) var(--space-4)',
            color: 'white',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: 'var(--space-4)',
        },
        arrow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-1) 0',
            color: 'var(--color-text-secondary)',
            fontSize: '16px',
        },
    };

    const getHandlerInfo = (handler) => {
        const handlers = {
            'AI_GENERATE_OPTIONS': { label: 'AI Generate', icon: '🤖', type: 'io' },
            'SHOW_HARDCODED_OPTIONS': { label: 'Show Options', icon: '📋', type: 'process' },
            'SHOW_COLOR_SWATCHES': { label: 'Colors', icon: '🎨', type: 'io' },
            'TRIGGER_UPLOAD': { label: 'Upload', icon: '📤', type: 'io' },
            'TRANSITION_PHASE': { label: 'Next Phase', icon: '➡️', type: 'terminal' },
            'SHOW_PLATFORM_OPTIONS': { label: 'Platforms', icon: '📱', type: 'process' },
            'SHOW_LOGO_CHECK': { label: 'Logo Check', icon: '🖼️', type: 'decision' },
            'SHOW_CONDITIONAL_DRILLDOWN': { label: 'Drill Down', icon: '🔍', type: 'decision' },
            'AI_GENERATE_SCRIPT': { label: 'Gen Script', icon: '📝', type: 'io' },
            'GENERATE_CASTING_IMAGES': { label: 'Gen Casting', icon: '👤', type: 'io' },
            'GENERATE_LOCATION_IMAGES': { label: 'Gen Locations', icon: '📍', type: 'io' },
            'SHOW_CASTING_IMAGE_OPTIONS': { label: 'Casting Opts', icon: '🎭', type: 'process' },
            'SHOW_LOCATION_IMAGE_OPTIONS': { label: 'Location Opts', icon: '🏞️', type: 'process' },
            'GENERATE_FIRST_FRAME_OPTIONS': { label: 'First Frame', icon: '🎬', type: 'io' },
            'GENERATE_LAST_FRAME_OPTIONS': { label: 'Last Frame', icon: '🎞️', type: 'io' },
            'COMPOSE_SCENE': { label: 'Compose', icon: '🎨', type: 'io' },
        };
        return handlers[handler] || { label: handler?.substring(0, 12) || '?', icon: '⚙️', type: 'process' };
    };

    // Render phase overview
    const renderOverview = () => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 'var(--space-4)' }}>
            <div style={styles.terminal}>🚀 Entry Point</div>

            {phaseOrder.map((phaseId, index) => {
                const phase = Object.values(phases).find(p => p.id === phaseId);
                const conditional = conditionalPhases[phaseId];
                if (!phase) return null;

                return (
                    <div key={phaseId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={styles.arrow}>↓</div>

                        {conditional && (
                            <div style={{ ...styles.decision, marginBottom: 'var(--space-2)' }}>
                                {conditional.condition}
                            </div>
                        )}

                        <div
                            style={{
                                ...styles.process,
                                cursor: 'pointer',
                                borderStyle: conditional ? 'dashed' : 'solid',
                            }}
                            onClick={() => setSelectedPhase(phaseId)}
                        >
                            <div style={{ fontWeight: 'bold' }}>{phase.emoji} {phase.title}</div>
                            {phase.persona && (
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                                    {phase.persona.name}
                                </div>
                            )}
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', marginTop: '2px' }}>
                                Click to expand →
                            </div>
                        </div>
                    </div>
                );
            })}

            <div style={styles.arrow}>↓</div>
            <div style={styles.terminal}>✨ Generation Complete</div>
        </div>
    );

    // Render detailed phase flow
    const renderPhaseDetail = (phaseId) => {
        const phase = Object.values(phases).find(p => p.id === phaseId);
        const phaseStepFlow = stepFlows[phaseId] || {};
        const phaseConditionalSteps = conditionalSteps[phaseId] || {};
        const conditional = conditionalPhases[phaseId];

        if (!phase) return null;

        const stepKeys = Object.keys(phaseStepFlow).filter(k => !k.startsWith('_')).sort((a, b) => parseInt(a) - parseInt(b));

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 'var(--space-4)' }}>
                {/* Phase Header */}
                <div style={styles.phaseHeader}>
                    {phase.emoji} {phase.title}
                    {phase.persona && <span style={{ opacity: 0.8, marginLeft: 'var(--space-2)' }}>({phase.persona.name})</span>}
                    {conditional && (
                        <div style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-1)', opacity: 0.9 }}>
                            Condition: {conditional.condition}
                        </div>
                    )}
                </div>

                {/* Start */}
                <div style={styles.terminal}>Start</div>

                {/* Steps */}
                {stepKeys.map((stepKey, index) => {
                    const step = phaseStepFlow[stepKey];
                    const conditionalStep = phaseConditionalSteps[stepKey];
                    const handler = step.onComplete?.handler;
                    const handlerInfo = handler ? getHandlerInfo(handler) : null;
                    const hasConditions = step.onComplete?.conditions;

                    return (
                        <div key={stepKey} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            <div style={styles.arrow}>↓</div>

                            {/* Conditional skip indicator */}
                            {conditionalStep && (
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-warning)',
                                    background: 'var(--color-warning-bg)',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-sm)',
                                    marginBottom: 'var(--space-1)',
                                }}>
                                    Skip if: {conditionalStep.condition} is false → Step {conditionalStep.skipTo}
                                </div>
                            )}

                            {/* Step Box */}
                            <div style={{
                                ...styles.process,
                                borderStyle: conditionalStep ? 'dashed' : 'solid',
                                background: conditionalStep ? 'var(--color-surface-hover)' : 'var(--color-surface)',
                            }}>
                                <div style={{ fontWeight: 'bold', fontSize: 'var(--font-size-sm)' }}>
                                    Step {stepKey}: {step.field}
                                </div>
                                {handlerInfo && (
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                                        {handlerInfo.icon} {handlerInfo.label}
                                        {step.onComplete?.targetField && (
                                            <span style={{ opacity: 0.7 }}> → {step.onComplete.targetField}</span>
                                        )}
                                    </div>
                                )}
                                {step.onComplete?.backgroundTask && (
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', marginTop: '2px' }}>
                                        ⏳ BG: {step.onComplete.backgroundTask.handler}
                                    </div>
                                )}
                            </div>

                            {/* Conditional branching */}
                            {hasConditions && (
                                <div style={{
                                    display: 'flex',
                                    gap: 'var(--space-4)',
                                    marginTop: 'var(--space-2)',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                }}>
                                    {step.onComplete.conditions.map((cond, ci) => {
                                        const condHandler = cond.handler ? getHandlerInfo(cond.handler) : null;
                                        return (
                                            <div key={ci} style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                padding: 'var(--space-2)',
                                                background: 'var(--color-surface-hover)',
                                                borderRadius: 'var(--radius-md)',
                                                minWidth: '120px',
                                            }}>
                                                <div style={{
                                                    ...styles.decision,
                                                    background: cond.if ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' : 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                                                    marginBottom: 'var(--space-1)',
                                                }}>
                                                    {cond.if ? `IF` : 'ELSE'}
                                                </div>
                                                {cond.if && (
                                                    <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginBottom: '4px', maxWidth: '100px', textAlign: 'center' }}>
                                                        {cond.if}
                                                    </div>
                                                )}
                                                {condHandler && (
                                                    <div style={{ fontSize: 'var(--font-size-xs)' }}>
                                                        {condHandler.icon} {condHandler.label}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)' }}>
                                                    → Step {cond.nextStep}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* End */}
                <div style={styles.arrow}>↓</div>
                <div style={styles.terminal}>End → Next Phase</div>
            </div>
        );
    };

    // Legend
    const renderLegend = () => (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-4)',
            padding: 'var(--space-3)',
            background: 'var(--color-surface-hover)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ ...styles.terminal, padding: '4px 12px', fontSize: '11px' }}>Start/End</div>
                <span style={{ fontSize: 'var(--font-size-xs)' }}>Terminal</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ ...styles.process, padding: '4px 12px', fontSize: '11px', minWidth: 'auto' }}>Step</div>
                <span style={{ fontSize: 'var(--font-size-xs)' }}>Process</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ ...styles.decision }}>IF/ELSE</div>
                <span style={{ fontSize: 'var(--font-size-xs)' }}>Decision</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ ...styles.io, padding: '4px 12px', fontSize: '11px' }}>
                    <span style={{ transform: 'skewX(5deg)', display: 'block' }}>🤖 AI</span>
                </div>
                <span style={{ fontSize: 'var(--font-size-xs)' }}>AI Generation</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ ...styles.process, borderStyle: 'dashed', padding: '4px 12px', fontSize: '11px', minWidth: 'auto' }}>Skip?</div>
                <span style={{ fontSize: 'var(--font-size-xs)' }}>Conditional</span>
            </div>
        </div>
    );

    return (
        <div>
            {/* Legend */}
            {renderLegend()}

            {/* Phase selector */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
                flexWrap: 'wrap',
            }}>
                <button
                    onClick={() => setSelectedPhase('all')}
                    style={{
                        padding: 'var(--space-1) var(--space-3)',
                        borderRadius: 'var(--radius-md)',
                        border: selectedPhase === 'all' ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                        background: selectedPhase === 'all' ? 'var(--color-primary)' : 'var(--color-surface)',
                        color: selectedPhase === 'all' ? 'white' : 'var(--color-text)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                    }}
                >
                    Overview
                </button>
                {phaseOrder.map(phaseId => {
                    const phase = Object.values(phases).find(p => p.id === phaseId);
                    if (!phase) return null;
                    return (
                        <button
                            key={phaseId}
                            onClick={() => setSelectedPhase(phaseId)}
                            style={{
                                padding: 'var(--space-1) var(--space-3)',
                                borderRadius: 'var(--radius-md)',
                                border: selectedPhase === phaseId ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                background: selectedPhase === phaseId ? 'var(--color-primary)' : 'var(--color-surface)',
                                color: selectedPhase === phaseId ? 'white' : 'var(--color-text)',
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)',
                            }}
                        >
                            {phase.emoji} {phase.title}
                        </button>
                    );
                })}
            </div>

            {/* Flowchart content */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid var(--color-border)',
                overflow: 'auto',
                maxHeight: '70vh',
            }}>
                {selectedPhase === 'all' ? renderOverview() : renderPhaseDetail(selectedPhase)}
            </div>

            {/* Handler reference */}
            {selectedPhase !== 'all' && (
                <div style={{
                    marginTop: 'var(--space-4)',
                    padding: 'var(--space-3)',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        Handler Types Used:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                        {Object.entries(stepSequencing._documentation?.handlers || {}).slice(0, 8).map(([h, desc]) => {
                            const info = getHandlerInfo(h);
                            return (
                                <div key={h} style={{
                                    fontSize: '11px',
                                    padding: '4px 8px',
                                    background: 'var(--color-surface-hover)',
                                    borderRadius: 'var(--radius-sm)',
                                }} title={desc}>
                                    {info.icon} {h}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminConversationFlow() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null);
    const [activeTab, setActiveTab] = useState('phases');
    const [expandedPhases, setExpandedPhases] = useState({});
    const [expandedFlowchartPhases, setExpandedFlowchartPhases] = useState({});
    const [expandedFlowchartSteps, setExpandedFlowchartSteps] = useState({});
    const [jsonError, setJsonError] = useState(null);

    // Drag and drop state
    const [draggedPhase, setDraggedPhase] = useState(null);
    const [draggedStep, setDraggedStep] = useState(null);
    const [dragOverPhase, setDragOverPhase] = useState(null);
    const [dragOverStep, setDragOverStep] = useState(null);
    const [showStepFlowEditor, setShowStepFlowEditor] = useState({});

    // Toggle flowchart phase expansion
    const toggleFlowchartPhase = (phaseId) => {
        setExpandedFlowchartPhases(prev => ({
            ...prev,
            [phaseId]: !prev[phaseId]
        }));
    };

    // Toggle flowchart step expansion
    const toggleFlowchartStep = (phaseId, stepIndex) => {
        const key = `${phaseId}-${stepIndex}`;
        setExpandedFlowchartSteps(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Helper function to convert phase ID to conversation flow phase key
    // Converts "creative_director" to "creativeDirectorPhase", "strategist" to "strategistPhase", etc.
    const getPhaseKey = (phaseId) => {
        // Convert snake_case to camelCase
        const camelCase = phaseId.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
        return `${camelCase}Phase`;
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch('http://localhost:3001/api/admin/conversation-flow');
            if (!res.ok) throw new Error('Failed to load config');
            const data = await res.json();
            setConfig(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const saveConfig = async () => {
        try {
            setSaveStatus('saving');
            const res = await fetch('http://localhost:3001/api/admin/conversation-flow', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            });
            if (!res.ok) throw new Error('Failed to save');
            setSaveStatus('success');
            setTimeout(() => setSaveStatus(null), 3000);
        } catch (err) {
            setSaveStatus('error');
            setError(err.message);
        }
    };

    const togglePhase = (phaseId) => {
        setExpandedPhases(prev => ({
            ...prev,
            [phaseId]: !prev[phaseId]
        }));
    };

    const updatePhase = (phaseId, field, value) => {
        setConfig(prev => ({
            ...prev,
            phases: {
                ...prev.phases,
                [phaseId]: {
                    ...prev.phases[phaseId],
                    [field]: value
                }
            }
        }));
    };

    const updatePhasePersona = (phaseId, field, value) => {
        setConfig(prev => ({
            ...prev,
            phases: {
                ...prev.phases,
                [phaseId]: {
                    ...prev.phases[phaseId],
                    persona: {
                        ...prev.phases[phaseId].persona,
                        [field]: value
                    }
                }
            }
        }));
    };

    const updateStepGreeting = (phaseId, stepIndex, value) => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            if (!newFlow[phaseKey]) return prev;

            const newSteps = [...newFlow[phaseKey].steps];
            const step = newSteps[stepIndex];

            if (step.greeting) {
                newSteps[stepIndex] = { ...step, greeting: { ...step.greeting, message: value } };
            } else if (step.optionsGeneration?.fallbackMessage) {
                newSteps[stepIndex] = {
                    ...step,
                    optionsGeneration: { ...step.optionsGeneration, fallbackMessage: value }
                };
            } else if (step.optionsGeneration?.fallback?.message) {
                newSteps[stepIndex] = {
                    ...step,
                    optionsGeneration: {
                        ...step.optionsGeneration,
                        fallback: { ...step.optionsGeneration.fallback, message: value }
                    }
                };
            } else {
                newSteps[stepIndex] = { ...step, message: value };
            }

            return {
                ...prev,
                conversationFlow: {
                    ...newFlow,
                    [phaseKey]: {
                        ...newFlow[phaseKey],
                        steps: newSteps
                    }
                }
            };
        });
    };

    const updateAudienceMessage = (phaseId, stepIndex, flowType, value) => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            const newSteps = [...newFlow[phaseKey].steps];
            const step = newSteps[stepIndex];

            if (step.audienceConfirmationFlows?.[flowType]) {
                newSteps[stepIndex] = {
                    ...step,
                    audienceConfirmationFlows: {
                        ...step.audienceConfirmationFlows,
                        [flowType]: {
                            ...step.audienceConfirmationFlows[flowType],
                            confirmationMessage: value
                        }
                    }
                };
            }

            return {
                ...prev,
                conversationFlow: { ...newFlow, [phaseKey]: { ...newFlow[phaseKey], steps: newSteps } }
            };
        });
    };

    const updateStepOption = (phaseId, stepIndex, optionIndex, field, value, listType = 'options') => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            const newSteps = [...newFlow[phaseKey].steps];
            const step = newSteps[stepIndex];

            const newList = [...(step[listType] || [])];
            newList[optionIndex] = { ...newList[optionIndex], [field]: value };

            newSteps[stepIndex] = { ...step, [listType]: newList };

            return {
                ...prev,
                conversationFlow: { ...newFlow, [phaseKey]: { ...newFlow[phaseKey], steps: newSteps } }
            };
        });
    };

    const addStepOption = (phaseId, stepIndex, listType = 'options') => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            const newSteps = [...newFlow[phaseKey].steps];
            const step = newSteps[stepIndex];

            const newList = [...(step[listType] || [])];
            newList.push({ text: 'New Option', value: 'new_value' });

            newSteps[stepIndex] = { ...step, [listType]: newList };

            return {
                ...prev,
                conversationFlow: { ...newFlow, [phaseKey]: { ...newFlow[phaseKey], steps: newSteps } }
            };
        });
    };

    const removeStepOption = (phaseId, stepIndex, optionIndex, listType = 'options') => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            const newSteps = [...newFlow[phaseKey].steps];
            const step = newSteps[stepIndex];

            const newList = step[listType].filter((_, i) => i !== optionIndex);

            newSteps[stepIndex] = { ...step, [listType]: newList };

            return {
                ...prev,
                conversationFlow: { ...newFlow, [phaseKey]: { ...newFlow[phaseKey], steps: newSteps } }
            };
        });
    };

    const addEntryPointOption = () => {
        setConfig(prev => ({
            ...prev,
            conversationFlow: {
                ...prev.conversationFlow,
                entryPoint: {
                    ...prev.conversationFlow.entryPoint,
                    options: [
                        ...(prev.conversationFlow.entryPoint?.options || []),
                        { text: 'New Option', value: 'new_value' }
                    ]
                }
            }
        }));
    };

    const updateEntryPointOption = (optionIndex, field, value) => {
        setConfig(prev => {
            const newOptions = [...(prev.conversationFlow.entryPoint?.options || [])];
            newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };

            return {
                ...prev,
                conversationFlow: {
                    ...prev.conversationFlow,
                    entryPoint: {
                        ...prev.conversationFlow.entryPoint,
                        options: newOptions
                    }
                }
            };
        });
    };

    const removeEntryPointOption = (optionIndex) => {
        setConfig(prev => {
            const newOptions = (prev.conversationFlow.entryPoint?.options || []).filter((_, i) => i !== optionIndex);

            return {
                ...prev,
                conversationFlow: {
                    ...prev.conversationFlow,
                    entryPoint: {
                        ...prev.conversationFlow.entryPoint,
                        options: newOptions
                    }
                }
            };
        });
    };

    const addStep = (phaseId) => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            if (!newFlow[phaseKey]) {
                // Initialize phase if it doesn't exist
                newFlow[phaseKey] = { steps: [] };
            }

            const newSteps = [...(newFlow[phaseKey].steps || [])];
            newSteps.push({
                step: newSteps.length,
                field: 'new_field',
                message: 'New question...',
                optionsGeneration: {
                    source: 'AI_GENERATED',
                    promptTemplate: ''
                }
            });

            return {
                ...prev,
                conversationFlow: {
                    ...newFlow,
                    [phaseKey]: {
                        ...newFlow[phaseKey],
                        steps: newSteps
                    }
                }
            };
        });
    };

    const removeStep = (phaseId, stepIndex) => {
        const phaseKey = getPhaseKey(phaseId);
        if (!window.confirm('Are you sure you want to delete this step?')) return;

        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            if (!newFlow[phaseKey]) return prev;

            const newSteps = newFlow[phaseKey].steps.filter((_, idx) => idx !== stepIndex);

            // Re-index steps
            const reindexedSteps = newSteps.map((step, idx) => ({
                ...step,
                step: idx
            }));

            return {
                ...prev,
                conversationFlow: {
                    ...newFlow,
                    [phaseKey]: {
                        ...newFlow[phaseKey],
                        steps: reindexedSteps
                    }
                }
            };
        });
    };

    const updateStepField = (phaseId, stepIndex, field, value) => {
        const phaseKey = getPhaseKey(phaseId);
        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            if (!newFlow[phaseKey]) return prev;

            const newSteps = [...newFlow[phaseKey].steps];
            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                [field]: value
            };

            return {
                ...prev,
                conversationFlow: {
                    ...newFlow,
                    [phaseKey]: {
                        ...newFlow[phaseKey],
                        steps: newSteps
                    }
                }
            };
        });
    };

    const handleJsonChange = (e) => {
        try {
            const parsed = JSON.parse(e.target.value);
            setConfig(parsed);
            setJsonError(null);
        } catch (err) {
            setJsonError(err.message);
        }
    };

    // ============================================
    // DRAG AND DROP HANDLERS FOR STEP REORDERING
    // ============================================

    const handleStepDragStart = (e, phaseId, stepIndex) => {
        setDraggedStep({ phaseId, stepIndex });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify({ phaseId, stepIndex }));
    };

    const handleStepDragOver = (e, phaseId, stepIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedStep && draggedStep.phaseId === phaseId && draggedStep.stepIndex !== stepIndex) {
            setDragOverStep({ phaseId, stepIndex });
        }
    };

    const handleStepDragLeave = () => {
        setDragOverStep(null);
    };

    const handleStepDrop = (e, targetPhaseId, targetStepIndex) => {
        e.preventDefault();
        if (!draggedStep || draggedStep.phaseId !== targetPhaseId) {
            setDraggedStep(null);
            setDragOverStep(null);
            return;
        }

        const sourceIndex = draggedStep.stepIndex;
        if (sourceIndex === targetStepIndex) {
            setDraggedStep(null);
            setDragOverStep(null);
            return;
        }

        const phaseKey = getPhaseKey(targetPhaseId);

        setConfig(prev => {
            const newFlow = { ...prev.conversationFlow };
            if (!newFlow[phaseKey]?.steps) return prev;

            const newSteps = [...newFlow[phaseKey].steps];
            const [movedStep] = newSteps.splice(sourceIndex, 1);
            newSteps.splice(targetStepIndex, 0, movedStep);

            // Re-index steps
            const reindexedSteps = newSteps.map((step, idx) => ({
                ...step,
                step: idx
            }));

            // Also update stepFlows in stepSequencing
            const newStepSequencing = { ...prev.stepSequencing };
            if (newStepSequencing.stepFlows?.[targetPhaseId]) {
                const oldStepFlows = newStepSequencing.stepFlows[targetPhaseId];
                const newStepFlows = {};

                // Reorder the step flows to match the new order
                reindexedSteps.forEach((step, newIdx) => {
                    const oldIdx = newSteps.indexOf(step) === newIdx
                        ? (sourceIndex < targetStepIndex
                            ? (newIdx >= sourceIndex && newIdx < targetStepIndex ? newIdx + 1 : (newIdx === targetStepIndex ? sourceIndex : newIdx))
                            : (newIdx > targetStepIndex && newIdx <= sourceIndex ? newIdx - 1 : (newIdx === targetStepIndex ? sourceIndex : newIdx)))
                        : newIdx;

                    // Find the original step flow for this step
                    const originalIdx = newIdx === targetStepIndex ? sourceIndex :
                        (sourceIndex < targetStepIndex ?
                            (newIdx >= sourceIndex && newIdx < targetStepIndex ? newIdx + 1 : newIdx) :
                            (newIdx > targetStepIndex && newIdx <= sourceIndex ? newIdx - 1 : newIdx));

                    if (oldStepFlows[originalIdx.toString()]) {
                        newStepFlows[newIdx.toString()] = {
                            ...oldStepFlows[originalIdx.toString()],
                            field: reindexedSteps[newIdx].field
                        };
                    }
                });

                newStepSequencing.stepFlows = {
                    ...newStepSequencing.stepFlows,
                    [targetPhaseId]: newStepFlows
                };
            }

            return {
                ...prev,
                conversationFlow: {
                    ...newFlow,
                    [phaseKey]: {
                        ...newFlow[phaseKey],
                        steps: reindexedSteps
                    }
                },
                stepSequencing: newStepSequencing
            };
        });

        setDraggedStep(null);
        setDragOverStep(null);
    };

    const handleStepDragEnd = () => {
        setDraggedStep(null);
        setDragOverStep(null);
    };

    // ============================================
    // PHASE ORDER DRAG AND DROP
    // ============================================

    const handlePhaseDragStart = (e, phaseIndex) => {
        setDraggedPhase(phaseIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handlePhaseDragOver = (e, phaseIndex) => {
        e.preventDefault();
        if (draggedPhase !== null && draggedPhase !== phaseIndex) {
            setDragOverPhase(phaseIndex);
        }
    };

    const handlePhaseDragLeave = () => {
        setDragOverPhase(null);
    };

    const handlePhaseDrop = (e, targetIndex) => {
        e.preventDefault();
        if (draggedPhase === null || draggedPhase === targetIndex) {
            setDraggedPhase(null);
            setDragOverPhase(null);
            return;
        }

        setConfig(prev => {
            if (!prev.stepSequencing?.phaseOrder) return prev;

            const newPhaseOrder = [...prev.stepSequencing.phaseOrder];
            const [movedPhase] = newPhaseOrder.splice(draggedPhase, 1);
            newPhaseOrder.splice(targetIndex, 0, movedPhase);

            return {
                ...prev,
                stepSequencing: {
                    ...prev.stepSequencing,
                    phaseOrder: newPhaseOrder
                }
            };
        });

        setDraggedPhase(null);
        setDragOverPhase(null);
    };

    // ============================================
    // STEP FLOW EDITOR HANDLERS
    // ============================================

    const toggleStepFlowEditor = (phaseId, stepIndex) => {
        const key = `${phaseId}-${stepIndex}`;
        setShowStepFlowEditor(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const updateStepFlowHandler = (phaseId, stepIndex, handler) => {
        setConfig(prev => {
            const newStepSequencing = { ...prev.stepSequencing };
            if (!newStepSequencing.stepFlows) {
                newStepSequencing.stepFlows = {};
            }
            if (!newStepSequencing.stepFlows[phaseId]) {
                newStepSequencing.stepFlows[phaseId] = {};
            }

            const currentFlow = newStepSequencing.stepFlows[phaseId][stepIndex.toString()] || {};

            newStepSequencing.stepFlows[phaseId][stepIndex.toString()] = {
                ...currentFlow,
                onComplete: {
                    ...(currentFlow.onComplete || {}),
                    handler
                }
            };

            return {
                ...prev,
                stepSequencing: newStepSequencing
            };
        });
    };

    const updateStepFlowNextStep = (phaseId, stepIndex, nextStep) => {
        setConfig(prev => {
            const newStepSequencing = { ...prev.stepSequencing };
            if (!newStepSequencing.stepFlows) {
                newStepSequencing.stepFlows = {};
            }
            if (!newStepSequencing.stepFlows[phaseId]) {
                newStepSequencing.stepFlows[phaseId] = {};
            }

            const currentFlow = newStepSequencing.stepFlows[phaseId][stepIndex.toString()] || {};

            newStepSequencing.stepFlows[phaseId][stepIndex.toString()] = {
                ...currentFlow,
                nextStep: nextStep === 'phase_complete' ? nextStep : parseInt(nextStep, 10)
            };

            return {
                ...prev,
                stepSequencing: newStepSequencing
            };
        });
    };

    const updateStepFlowTargetField = (phaseId, stepIndex, targetField) => {
        setConfig(prev => {
            const newStepSequencing = { ...prev.stepSequencing };
            if (!newStepSequencing.stepFlows?.[phaseId]?.[stepIndex.toString()]) return prev;

            newStepSequencing.stepFlows[phaseId][stepIndex.toString()] = {
                ...newStepSequencing.stepFlows[phaseId][stepIndex.toString()],
                onComplete: {
                    ...newStepSequencing.stepFlows[phaseId][stepIndex.toString()].onComplete,
                    targetField
                }
            };

            return {
                ...prev,
                stepSequencing: newStepSequencing
            };
        });
    };

    const updateStepFlowCondition = (phaseId, stepIndex, condition) => {
        setConfig(prev => {
            const newStepSequencing = { ...prev.stepSequencing };
            if (!newStepSequencing.stepFlows?.[phaseId]?.[stepIndex.toString()]) return prev;

            newStepSequencing.stepFlows[phaseId][stepIndex.toString()] = {
                ...newStepSequencing.stepFlows[phaseId][stepIndex.toString()],
                condition: condition || undefined
            };

            return {
                ...prev,
                stepSequencing: newStepSequencing
            };
        });
    };

    // Available handlers for dropdown
    const availableHandlers = [
        'AI_GENERATE_OPTIONS',
        'SHOW_HARDCODED_OPTIONS',
        'SHOW_COLOR_SWATCHES',
        'TRIGGER_UPLOAD',
        'SHOW_CONFIRMATION',
        'TRANSITION_PHASE',
        'SHOW_PLATFORM_OPTIONS',
        'CHECK_LOGO',
        'SHOW_ASSET_OPTIONS',
        'SHOW_CONDITIONAL_DRILLDOWN',
        'AI_GENERATE_SCRIPT',
        'GENERATE_FIRST_FRAME_OPTIONS',
        'GENERATE_LAST_FRAME_OPTIONS'
    ];

    // Extract context variables from a prompt template
    const extractContextVariables = (promptTemplate) => {
        if (!promptTemplate) return [];

        // Match patterns like {variable}, ${variable}, or variable names in the prompt
        const variables = new Set();

        // Match curly brace patterns: {product}, ${creativeBrief.product}
        const curlyBraceMatches = promptTemplate.match(/\$?\{([^}]+)\}/g);
        if (curlyBraceMatches) {
            curlyBraceMatches.forEach(match => {
                const varName = match.replace(/\$?\{|\}/g, '');
                variables.add(varName);
            });
        }

        // Also look for common brief field references in the text
        const commonFields = [
            'product', 'targetAudience', 'desiredEmotion', 'objective',
            'selectedHeadline', 'tone', 'cta', 'visualStyle', 'imageConcept',
            'creativeBrief.product', 'creativeBrief.targetAudience',
            'selectedBrand?.name', 'selectedBrand?.voice?.tone',
            'branding.name', 'branding.voice.tone'
        ];

        commonFields.forEach(field => {
            if (promptTemplate.includes(field)) {
                variables.add(field);
            }
        });

        return Array.from(variables);
    };

    // Get a human-readable description of a context variable
    const getVariableDescription = (varName) => {
        const descriptions = {
            'product': 'Product/service name from creative brief',
            'creativeBrief.product': 'Product/service name from creative brief',
            'targetAudience': 'Target audience from creative brief',
            'creativeBrief.targetAudience': 'Target audience from creative brief',
            'desiredEmotion': 'Desired emotional response from creative brief',
            'selectedBrand?.name': 'Brand name from selected brand',
            'branding.name': 'Brand name from branding context',
            'selectedBrand?.voice?.tone': 'Brand voice tone from selected brand',
            'branding.voice.tone': 'Brand voice tone from branding context',
            'selectedHeadline': 'Selected headline from creative brief',
            'tone': 'Copy tone from creative brief',
            'cta': 'Call-to-action from creative brief',
            'visualStyle': 'Visual style from creative brief',
            'imageConcept': 'Image concept from creative brief',
            'objective': 'Campaign objective from creative brief',
            'creativeBrief.desiredEmotion': 'Desired emotional response from creative brief',
            'creativeBrief.selectedHeadline': 'Selected headline from creative brief',
            'creativeBrief.tone': 'Copy tone from creative brief',
            'creativeBrief.cta': 'Call-to-action from creative brief',
            'creativeBrief.visualStyle': 'Visual style from creative brief',
            'creativeBrief.imageType': 'Image type from creative brief',
            'creativeBrief.imageConcept': 'Image concept from creative brief',
            'creativeBrief.objective': 'Campaign objective from creative brief',
            'selectedBrand?.industry': 'Brand industry from selected brand',
            'creativeBrief.videoSettings?.videoLength': 'Video duration (6s or 8s)',
            'creativeBrief.videoSettings?.videoStyle': 'Video style (cinematic, commercial, etc.)',
            'creativeBrief.videoSettings?.videoType': 'Video type (product_highlight, actor_driven, mixed)',
            'creativeBrief.videoSettings?.actorCount': 'Number of actors in the video',
            'creativeBrief.videoSettings?.actorDescription': 'Description of actors/talent'
        };

        return descriptions[varName] || `Variable: ${varName}`;
    };

    // Get the actual context variables used for a specific step based on implementation
    const getStepContextMapping = (phaseId, field) => {
        // This maps to the actual implementation in ConversationalCreative.jsx
        const contextMappings = {
            'strategist': {
                'targetAudience': [
                    'creativeBrief.product',
                    'selectedBrand?.name',
                    'selectedBrand?.industry'
                ],
                'desiredEmotion': [
                    'creativeBrief.product',
                    'creativeBrief.targetAudience',
                    'selectedBrand?.name',
                    'selectedBrand?.voice?.tone'
                ]
            },
            'copywriter': {
                'selectedHeadline': [
                    'creativeBrief.product',
                    'creativeBrief.targetAudience',
                    'creativeBrief.desiredEmotion',
                    'creativeBrief.objective',
                    'selectedBrand?.name',
                    'selectedBrand?.voice?.tone'
                ],
                'cta': [
                    'creativeBrief.product',
                    'creativeBrief.objective',
                    'creativeBrief.selectedHeadline',
                    'selectedBrand?.name'
                ]
            },
            'designer': {
                'imageConcept': [
                    'creativeBrief.product',
                    'creativeBrief.targetAudience',
                    'creativeBrief.desiredEmotion',
                    'creativeBrief.selectedHeadline',
                    'creativeBrief.visualStyle',
                    'creativeBrief.imageType',
                    'selectedBrand?.name'
                ]
            },
            'video_director': {
                'actorDescription': [
                    'creativeBrief.imageConcept',
                    'creativeBrief.targetAudience',
                    'creativeBrief.videoSettings?.videoStyle',
                    'creativeBrief.videoSettings?.actorCount'
                ],
                'scriptContent': [
                    'creativeBrief.product',
                    'creativeBrief.targetAudience',
                    'creativeBrief.desiredEmotion',
                    'creativeBrief.selectedHeadline',
                    'creativeBrief.cta',
                    'creativeBrief.videoSettings?.videoLength',
                    'creativeBrief.videoSettings?.videoStyle',
                    'creativeBrief.videoSettings?.videoType',
                    'creativeBrief.videoSettings?.actorCount',
                    'creativeBrief.videoSettings?.actorDescription',
                    'creativeBrief.imageConcept'
                ]
            }
        };

        return contextMappings[phaseId]?.[field] || [];
    };

    if (loading) return (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            Loading configuration...
        </div>
    );

    if (error) return (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-error)' }}>
            Error: {error}
        </div>
    );

    return (
        <div style={{
            minHeight: '100vh',
            background: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
            padding: 'var(--space-8)'
        }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <header style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-8)'
                }}>
                    <div>
                        <h1 style={{
                            fontSize: 'var(--font-size-3xl)',
                            fontWeight: 'bold',
                            background: 'var(--gradient-primary)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            marginBottom: 'var(--space-2)'
                        }}>
                            Conversation Flow Admin
                        </h1>
                        <p style={{ color: 'var(--color-text-secondary)' }}>Manage personas, questions, and logic</p>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                        <button
                            onClick={fetchConfig}
                            style={{
                                padding: 'var(--space-2)',
                                borderRadius: 'var(--radius-lg)',
                                background: 'var(--color-surface)',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)'
                            }}
                            title="Refresh"
                        >
                            <RefreshCw size={20} />
                        </button>
                        <button
                            onClick={saveConfig}
                            disabled={saveStatus === 'saving' || jsonError}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-6)',
                                borderRadius: 'var(--radius-lg)',
                                fontWeight: 500,
                                border: 'none',
                                cursor: saveStatus === 'saving' || jsonError ? 'not-allowed' : 'pointer',
                                background: saveStatus === 'success' ? 'var(--color-success)' :
                                    saveStatus === 'error' ? 'var(--color-error)' :
                                        'var(--color-primary)',
                                color: 'white',
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            {saveStatus === 'saving' ? <RefreshCw className="animate-spin" size={20} /> :
                                saveStatus === 'success' ? <Check size={20} /> :
                                    <Save size={20} />}
                            {saveStatus === 'saving' ? 'Saving...' :
                                saveStatus === 'success' ? 'Saved!' :
                                    'Save Changes'}
                        </button>
                    </div>
                </header>

                <div style={{
                    display: 'flex',
                    gap: 'var(--space-4)',
                    marginBottom: 'var(--space-8)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    {['phases', 'flowchart', 'prompts', 'raw'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: 'var(--space-2) var(--space-4)',
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                background: 'transparent',
                                border: 'none',
                                borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
                                color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {false && activeTab === 'OLD_PHASES_REMOVED' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                        {/* Entry Point Section */}
                        <div style={{
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-xl)',
                            overflow: 'hidden',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div
                                onClick={() => togglePhase('entryPoint')}
                                style={{
                                    padding: 'var(--space-4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    transition: 'background var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-surface-hover)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                                    {expandedPhases['entryPoint'] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                    <span style={{ fontSize: 'var(--font-size-2xl)' }}>👋</span>
                                    <div>
                                        <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold', margin: 0 }}>Entry Point</h3>
                                        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0 }}>Initial greeting and flow selection</p>
                                    </div>
                                </div>
                            </div>

                            {expandedPhases['entryPoint'] && (
                                <div style={{
                                    padding: 'var(--space-6)',
                                    borderTop: '1px solid var(--color-border)',
                                    background: 'var(--color-bg-secondary)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 'var(--space-4)'
                                }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Greeting Message</label>
                                        <textarea
                                            value={config.conversationFlow.entryPoint?.message || ''}
                                            onChange={(e) => {
                                                setConfig(prev => ({
                                                    ...prev,
                                                    conversationFlow: {
                                                        ...prev.conversationFlow,
                                                        entryPoint: {
                                                            ...prev.conversationFlow.entryPoint,
                                                            message: e.target.value
                                                        }
                                                    }
                                                }));
                                            }}
                                            autoComplete="off"
                                            data-form-type="other"
                                            style={{
                                                width: '100%',
                                                background: 'var(--color-surface)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                padding: 'var(--space-2)',
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-text-primary)',
                                                outline: 'none',
                                                minHeight: '80px'
                                            }}
                                        />
                                    </div>

                                    {/* Entry Point Options */}
                                    {config.conversationFlow.entryPoint?.options && (
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                                <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Options</label>
                                                <button onClick={addEntryPointOption} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>+ Add Option</button>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                {config.conversationFlow.entryPoint.options.map((opt, optIdx) => (
                                                    <div key={optIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                                                        <input
                                                            type="text"
                                                            value={opt.text}
                                                            onChange={(e) => updateEntryPointOption(optIdx, 'text', e.target.value)}
                                                            placeholder="Text"
                                                            autoComplete="off"
                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={opt.value}
                                                            onChange={(e) => updateEntryPointOption(optIdx, 'value', e.target.value)}
                                                            placeholder="Value"
                                                            autoComplete="off"
                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                                        />
                                                        <button onClick={() => removeEntryPointOption(optIdx)} style={{ background: 'transparent', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {Object.entries(config.phases).map(([key, phase]) => (
                            <div key={key} style={{
                                background: 'var(--color-surface)',
                                borderRadius: 'var(--radius-xl)',
                                overflow: 'hidden',
                                border: '1px solid var(--color-border)'
                            }}>
                                <div
                                    onClick={() => togglePhase(key)}
                                    style={{
                                        padding: 'var(--space-4)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        transition: 'background var(--transition-fast)'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-surface-hover)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                                        {expandedPhases[key] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                        <span style={{ fontSize: 'var(--font-size-2xl)' }}>{phase.emoji}</span>
                                        <div>
                                            <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold', margin: 0 }}>{phase.title}</h3>
                                            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0 }}>{phase.subtitle}</p>
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-tertiary)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontFamily: 'monospace'
                                    }}>
                                        {key}
                                    </div>
                                </div>

                                {expandedPhases[key] && (
                                    <div style={{
                                        padding: 'var(--space-6)',
                                        borderTop: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-secondary)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 'var(--space-8)'
                                    }}>
                                        {/* Phase Details */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                                <h4 style={{
                                                    fontWeight: 600,
                                                    color: 'var(--color-primary)',
                                                    textTransform: 'uppercase',
                                                    fontSize: 'var(--font-size-xs)',
                                                    letterSpacing: '0.05em',
                                                    margin: 0
                                                }}>Phase Settings</h4>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Title</label>
                                                    <input
                                                        type="text"
                                                        value={phase.title}
                                                        onChange={(e) => updatePhase(key, 'title', e.target.value)}
                                                        autoComplete="off"
                                                        data-form-type="other"
                                                        style={{
                                                            width: '100%',
                                                            background: 'var(--color-bg-primary)',
                                                            border: '1px solid var(--color-border)',
                                                            borderRadius: 'var(--radius-md)',
                                                            padding: 'var(--space-2)',
                                                            fontSize: 'var(--font-size-sm)',
                                                            color: 'var(--color-text-primary)',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Subtitle</label>
                                                    <input
                                                        type="text"
                                                        value={phase.subtitle}
                                                        onChange={(e) => updatePhase(key, 'subtitle', e.target.value)}
                                                        autoComplete="off"
                                                        data-form-type="other"
                                                        style={{
                                                            width: '100%',
                                                            background: 'var(--color-bg-primary)',
                                                            border: '1px solid var(--color-border)',
                                                            borderRadius: 'var(--radius-md)',
                                                            padding: 'var(--space-2)',
                                                            fontSize: 'var(--font-size-sm)',
                                                            color: 'var(--color-text-primary)',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {phase.persona && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                                    <h4 style={{
                                                        fontWeight: 600,
                                                        color: 'var(--color-accent)',
                                                        textTransform: 'uppercase',
                                                        fontSize: 'var(--font-size-xs)',
                                                        letterSpacing: '0.05em',
                                                        margin: 0
                                                    }}>Persona ({phase.persona.name})</h4>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Name</label>
                                                        <input
                                                            type="text"
                                                            value={phase.persona.name}
                                                            onChange={(e) => updatePhasePersona(key, 'name', e.target.value)}
                                                            autoComplete="off"
                                                            data-form-type="other"
                                                            style={{
                                                                width: '100%',
                                                                background: 'var(--color-bg-primary)',
                                                                border: '1px solid var(--color-border)',
                                                                borderRadius: 'var(--radius-md)',
                                                                padding: 'var(--space-2)',
                                                                fontSize: 'var(--font-size-sm)',
                                                                color: 'var(--color-text-primary)',
                                                                outline: 'none'
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Tone</label>
                                                        <input
                                                            type="text"
                                                            value={phase.persona.tone}
                                                            onChange={(e) => updatePhasePersona(key, 'tone', e.target.value)}
                                                            autoComplete="off"
                                                            data-form-type="other"
                                                            style={{
                                                                width: '100%',
                                                                background: 'var(--color-bg-primary)',
                                                                border: '1px solid var(--color-border)',
                                                                borderRadius: 'var(--radius-md)',
                                                                padding: 'var(--space-2)',
                                                                fontSize: 'var(--font-size-sm)',
                                                                color: 'var(--color-text-primary)',
                                                                outline: 'none'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Conversation Steps */}
                                        {config.conversationFlow[getPhaseKey(phase.id)] && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                                <h4 style={{
                                                    fontWeight: 600,
                                                    color: 'var(--color-success)',
                                                    textTransform: 'uppercase',
                                                    fontSize: 'var(--font-size-xs)',
                                                    letterSpacing: '0.05em',
                                                    marginBottom: 'var(--space-4)',
                                                    margin: 0
                                                }}>
                                                    Conversation Steps
                                                </h4>
                                                {config.conversationFlow[getPhaseKey(phase.id)].steps?.map((step, idx) => (
                                                    <div key={idx} style={{
                                                        background: 'var(--color-bg-primary)',
                                                        borderRadius: 'var(--radius-lg)',
                                                        padding: 'var(--space-4)',
                                                        border: '1px solid var(--color-border)'
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                                <span style={{
                                                                    fontSize: 'var(--font-size-xs)',
                                                                    fontFamily: 'monospace',
                                                                    background: 'var(--color-surface)',
                                                                    padding: 'var(--space-1) var(--space-2)',
                                                                    borderRadius: 'var(--radius-sm)',
                                                                    color: 'var(--color-text-secondary)'
                                                                }}>
                                                                    Step {idx + 1}
                                                                </span>
                                                                <input
                                                                    type="text"
                                                                    value={step.field}
                                                                    onChange={(e) => updateStepField(phase.id, idx, 'field', e.target.value)}
                                                                    placeholder="Field Name"
                                                                    autoComplete="off"
                                                                    data-form-type="other"
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        borderBottom: '1px solid var(--color-border)',
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontFamily: 'monospace',
                                                                        color: 'var(--color-text-primary)',
                                                                        width: '150px'
                                                                    }}
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={() => removeStep(phase.id, idx)}
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    color: 'var(--color-error)',
                                                                    cursor: 'pointer',
                                                                    padding: 'var(--space-1)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    opacity: 0.7,
                                                                    transition: 'opacity 0.2s'
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                                                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
                                                                title="Remove Step"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>

                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>
                                                                    {step.audienceConfirmationFlows ? 'Confirmation Messages (Dynamic)' : 'Message / Question'}
                                                                </label>

                                                                {step.audienceConfirmationFlows ? (
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                        <div>
                                                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>URL Inference Message</span>
                                                                            <textarea
                                                                                value={step.audienceConfirmationFlows.urlInferenceFlow?.confirmationMessage || ''}
                                                                                onChange={(e) => updateAudienceMessage(phase.id, idx, 'urlInferenceFlow', e.target.value)}
                                                                                autoComplete="off"
                                                                                data-form-type="other"
                                                                                style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', outline: 'none', minHeight: '60px' }}
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Brand Audience Message</span>
                                                                            <textarea
                                                                                value={step.audienceConfirmationFlows.brandAudienceFlow?.confirmationMessage || ''}
                                                                                onChange={(e) => updateAudienceMessage(phase.id, idx, 'brandAudienceFlow', e.target.value)}
                                                                                autoComplete="off"
                                                                                data-form-type="other"
                                                                                style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', outline: 'none', minHeight: '60px' }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <textarea
                                                                        value={step.greeting?.message || step.message || step.optionsGeneration?.fallbackMessage || step.optionsGeneration?.fallback?.message || ''}
                                                                        onChange={(e) => updateStepGreeting(phase.id, idx, e.target.value)}
                                                                        autoComplete="off"
                                                                        data-form-type="other"
                                                                        style={{
                                                                            width: '100%',
                                                                            background: 'var(--color-surface)',
                                                                            border: '1px solid var(--color-border)',
                                                                            borderRadius: 'var(--radius-md)',
                                                                            padding: 'var(--space-2)',
                                                                            fontSize: 'var(--font-size-sm)',
                                                                            color: 'var(--color-text-primary)',
                                                                            outline: 'none',
                                                                            minHeight: '80px'
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>

                                                            {/* Options Editor */}
                                                            {(step.options || step.alwaysInclude) && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                                                    {step.options && (
                                                                        <div>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                                                                <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Options</label>
                                                                                <button onClick={() => addStepOption(phase.id, idx, 'options')} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>+ Add Option</button>
                                                                            </div>
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                {step.options.map((opt, optIdx) => (
                                                                                    <div key={optIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={opt.text}
                                                                                            onChange={(e) => updateStepOption(phase.id, idx, optIdx, 'text', e.target.value, 'options')}
                                                                                            placeholder="Text"
                                                                                            autoComplete="off"
                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}
                                                                                        />
                                                                                        <input
                                                                                            type="text"
                                                                                            value={opt.value}
                                                                                            onChange={(e) => updateStepOption(phase.id, idx, optIdx, 'value', e.target.value, 'options')}
                                                                                            placeholder="Value"
                                                                                            autoComplete="off"
                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                                                                        />
                                                                                        <button onClick={() => removeStepOption(phase.id, idx, optIdx, 'options')} style={{ background: 'transparent', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {step.alwaysInclude && (
                                                                        <div>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                                                                <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Always Include Options</label>
                                                                                <button onClick={() => addStepOption(phase.id, idx, 'alwaysInclude')} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>+ Add Option</button>
                                                                            </div>
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                {step.alwaysInclude.map((opt, optIdx) => (
                                                                                    <div key={optIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={opt.text}
                                                                                            onChange={(e) => updateStepOption(phase.id, idx, optIdx, 'text', e.target.value, 'alwaysInclude')}
                                                                                            placeholder="Text"
                                                                                            autoComplete="off"
                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}
                                                                                        />
                                                                                        <input
                                                                                            type="text"
                                                                                            value={opt.value}
                                                                                            onChange={(e) => updateStepOption(phase.id, idx, optIdx, 'value', e.target.value, 'alwaysInclude')}
                                                                                            placeholder="Value"
                                                                                            autoComplete="off"
                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                                                                        />
                                                                                        <button onClick={() => removeStepOption(phase.id, idx, optIdx, 'alwaysInclude')} style={{ background: 'transparent', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {step.optionsGeneration && (
                                                                <div style={{
                                                                    background: 'rgba(124, 58, 237, 0.05)',
                                                                    padding: 'var(--space-3)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    border: '1px solid rgba(124, 58, 237, 0.1)'
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-primary)', marginBottom: 'var(--space-2)' }}>
                                                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold' }}>AI Generated Options</span>
                                                                    </div>
                                                                    {step.optionsGeneration.promptTemplate !== undefined && (() => {
                                                                        const promptTemplate = step.optionsGeneration.promptTemplate;
                                                                        const extractedVars = extractContextVariables(promptTemplate);
                                                                        const mappedVars = getStepContextMapping(phase.id, step.field);
                                                                        const isFromMapping = mappedVars && mappedVars.length > 0;
                                                                        const contextVars = isFromMapping ? mappedVars : extractedVars;

                                                                        return (
                                                                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                                                                <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Prompt Template</label>
                                                                                <textarea
                                                                                    value={promptTemplate}
                                                                                    onChange={(e) => {
                                                                                        const newFlow = { ...config.conversationFlow };
                                                                                        newFlow[getPhaseKey(phase.id)].steps[idx].optionsGeneration.promptTemplate = e.target.value;
                                                                                        setConfig({ ...config, conversationFlow: newFlow });
                                                                                    }}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        background: 'var(--color-surface)',
                                                                                        border: '1px solid var(--color-border)',
                                                                                        borderRadius: 'var(--radius-md)',
                                                                                        padding: 'var(--space-2)',
                                                                                        fontSize: 'var(--font-size-sm)',
                                                                                        color: 'var(--color-text-primary)',
                                                                                        outline: 'none',
                                                                                        minHeight: '100px',
                                                                                        marginBottom: 'var(--space-2)'
                                                                                    }}
                                                                                />
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 'var(--space-1)',
                                                                                    marginBottom: 'var(--space-2)'
                                                                                }}>
                                                                                    <span>📊</span>
                                                                                    <span>AI Context (Dynamic Variables)</span>
                                                                                    {isFromMapping && (
                                                                                        <span style={{
                                                                                            fontSize: 'var(--font-size-xs)',
                                                                                            fontWeight: 'normal',
                                                                                            color: 'var(--color-text-tertiary)',
                                                                                            fontStyle: 'italic',
                                                                                            marginLeft: 'var(--space-1)'
                                                                                        }}>
                                                                                            (from implementation)
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                                                                    {contextVars.map((varName, vIdx) => (
                                                                                        <div key={vIdx} style={{
                                                                                            fontSize: 'var(--font-size-xs)',
                                                                                            color: 'var(--color-text-secondary)',
                                                                                            display: 'flex',
                                                                                            alignItems: 'flex-start',
                                                                                            gap: 'var(--space-2)'
                                                                                        }}>
                                                                                            <code style={{
                                                                                                background: 'var(--color-surface)',
                                                                                                padding: '2px 6px',
                                                                                                borderRadius: 'var(--radius-sm)',
                                                                                                fontFamily: 'monospace',
                                                                                                fontSize: 'var(--font-size-xs)',
                                                                                                color: 'var(--color-primary)',
                                                                                                whiteSpace: 'nowrap'
                                                                                            }}>
                                                                                                {varName}
                                                                                            </code>
                                                                                            <span style={{ flex: 1 }}>
                                                                                                {getVariableDescription(varName)}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()}

                                                                    {/* Conditional Generation Prompts (keyed by previous selection) */}
                                                                    {step.optionsGeneration.generationPrompt && typeof step.optionsGeneration.generationPrompt === 'object' && (
                                                                        <div style={{ marginBottom: 'var(--space-3)' }}>
                                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                                                                                Conditional Prompts (based on previous selection)
                                                                            </label>
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                {Object.entries(step.optionsGeneration.generationPrompt).map(([conditionKey, promptText]) => (
                                                                                    <div key={conditionKey} style={{
                                                                                        background: 'var(--color-surface)',
                                                                                        borderRadius: 'var(--radius-md)',
                                                                                        border: '1px solid var(--color-border)',
                                                                                        overflow: 'hidden'
                                                                                    }}>
                                                                                        <div style={{
                                                                                            padding: 'var(--space-2)',
                                                                                            background: 'rgba(124, 58, 237, 0.1)',
                                                                                            borderBottom: '1px solid var(--color-border)',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 'var(--space-2)'
                                                                                        }}>
                                                                                            <span style={{ fontSize: 'var(--font-size-xs)' }}>🔀</span>
                                                                                            <code style={{
                                                                                                fontSize: 'var(--font-size-xs)',
                                                                                                color: 'var(--color-primary)',
                                                                                                fontWeight: 'bold'
                                                                                            }}>
                                                                                                {conditionKey}
                                                                                            </code>
                                                                                        </div>
                                                                                        <textarea
                                                                                            value={promptText}
                                                                                            onChange={(e) => {
                                                                                                const newFlow = { ...config.conversationFlow };
                                                                                                newFlow[getPhaseKey(phase.id)].steps[idx].optionsGeneration.generationPrompt[conditionKey] = e.target.value;
                                                                                                setConfig({ ...config, conversationFlow: newFlow });
                                                                                            }}
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                background: 'transparent',
                                                                                                border: 'none',
                                                                                                padding: 'var(--space-2)',
                                                                                                fontSize: 'var(--font-size-xs)',
                                                                                                color: 'var(--color-text-secondary)',
                                                                                                outline: 'none',
                                                                                                minHeight: '80px',
                                                                                                resize: 'vertical',
                                                                                                fontFamily: 'inherit'
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                            {step.optionsGeneration.basedOn && (
                                                                                <div style={{ marginTop: 'var(--space-2)' }}>
                                                                                    <div style={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 'var(--space-1)',
                                                                                        marginBottom: 'var(--space-1)'
                                                                                    }}>
                                                                                        <span>📊</span>
                                                                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Context Variables</span>
                                                                                    </div>
                                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                                        {step.optionsGeneration.basedOn.map((varName, vIdx) => (
                                                                                            <code key={vIdx} style={{
                                                                                                background: 'var(--color-surface)',
                                                                                                padding: '2px 6px',
                                                                                                borderRadius: 'var(--radius-sm)',
                                                                                                fontFamily: 'monospace',
                                                                                                fontSize: 'var(--font-size-xs)',
                                                                                                color: 'var(--color-primary)'
                                                                                            }}>
                                                                                                {varName}
                                                                                            </code>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Casting Generation Prompt - inline with actors step */}
                                                            {step.field === 'videoSettings.actors' && (
                                                                <div style={{
                                                                    background: 'rgba(236, 72, 153, 0.05)',
                                                                    padding: 'var(--space-3)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    border: '1px solid rgba(236, 72, 153, 0.2)',
                                                                    marginTop: 'var(--space-2)'
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: '#ec4899' }}>🎭 Casting Generation Prompt</span>
                                                                        <span style={{
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            background: 'rgba(124, 58, 237, 0.1)',
                                                                            color: 'var(--color-primary)',
                                                                            padding: '2px 6px',
                                                                            borderRadius: 'var(--radius-sm)'
                                                                        }}>from code</span>
                                                                    </div>
                                                                    <pre style={{
                                                                        background: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderRadius: 'var(--radius-md)',
                                                                        padding: 'var(--space-3)',
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontFamily: 'monospace',
                                                                        color: 'var(--color-text-secondary)',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        margin: 0,
                                                                        maxHeight: '300px',
                                                                        overflow: 'auto'
                                                                    }}>
{`You are a casting director for a video advertisement. Generate 4 DISTINCT options for the MAIN CHARACTER of this video ad.

VIDEO CONTEXT:
- Product: {product}
- Target Audience: {targetAudience}
- Marketing Angle: {marketingAngle}
- Visual Style: {visualStyle}
- Tone: {tone}
- Desired Emotion: {desiredEmotion}
  * The main character should embody and evoke this emotional tone

You are casting ONE main character. Each option should represent a distinctly different casting direction. Consider:
- Different ages within the target demographic
- Different styles (casual vs professional vs athletic vs creative)
- Different energy levels (calm/confident vs energetic/dynamic)
- Different looks that appeal to different audience segments
- The character should feel authentic and relatable to the target audience

OUTPUT FORMAT:
[{ id, title, description, imagePrompt }, ...]

4 images are generated showing different main character options.`}
                                                                    </pre>
                                                                    <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                        {['product', 'targetAudience', 'marketingAngle', 'visualStyle', 'tone', 'desiredEmotion'].map(v => (
                                                                            <code key={v} style={{
                                                                                background: 'var(--color-surface)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 'var(--radius-sm)',
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '10px',
                                                                                color: 'var(--color-primary)'
                                                                            }}>{v}</code>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Location Scouting Prompt - inline with scenery step */}
                                                            {step.field === 'videoSettings.scenery' && (
                                                                <div style={{
                                                                    background: 'rgba(16, 185, 129, 0.05)',
                                                                    padding: 'var(--space-3)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                                                    marginTop: 'var(--space-2)'
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: '#10b981' }}>📍 Location Scouting Prompt</span>
                                                                        <span style={{
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            background: 'rgba(124, 58, 237, 0.1)',
                                                                            color: 'var(--color-primary)',
                                                                            padding: '2px 6px',
                                                                            borderRadius: 'var(--radius-sm)'
                                                                        }}>from code</span>
                                                                    </div>
                                                                    <pre style={{
                                                                        background: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderRadius: 'var(--radius-md)',
                                                                        padding: 'var(--space-3)',
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontFamily: 'monospace',
                                                                        color: 'var(--color-text-secondary)',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        margin: 0,
                                                                        maxHeight: '300px',
                                                                        overflow: 'auto'
                                                                    }}>
{`You are a location scout for a video advertisement. Generate 4 DISTINCT location options.

VIDEO CONTEXT:
- Product: {product}
- Target Audience: {targetAudience}
- Marketing Angle: {marketingAngle}
- Visual Style: {visualStyle}
- Video Style: {videoStyle}
- Visual Concept: {visualConcept}
- Desired Emotion: {desiredEmotion}
  * Location should create atmosphere that evokes this emotion

Each location should represent a distinctly different setting. Consider:
- Different environments (indoor vs outdoor, urban vs natural)
- Different moods (sleek/modern vs warm/cozy vs bold/dramatic)
- Different contexts (workplace vs home vs public vs aspirational)
- Different times of day and lighting conditions

OUTPUT FORMAT:
[{ id, title, description, imagePrompt }, ...]

Images are generated as location reference photos.`}
                                                                    </pre>
                                                                    <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                        {['product', 'targetAudience', 'marketingAngle', 'visualStyle', 'videoStyle', 'visualConcept', 'desiredEmotion'].map(v => (
                                                                            <code key={v} style={{
                                                                                background: 'var(--color-surface)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 'var(--radius-sm)',
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '10px',
                                                                                color: 'var(--color-primary)'
                                                                            }}>{v}</code>
                                                                        ))}
                                                                    </div>

                                                                    {/* Combined Scene Prompt - shown after location since it combines casting + location */}
                                                                    <div style={{
                                                                        marginTop: 'var(--space-3)',
                                                                        paddingTop: 'var(--space-3)',
                                                                        borderTop: '1px dashed var(--color-border)'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: '#8b5cf6' }}>🎬 Combined Scene Prompt</span>
                                                                            <span style={{
                                                                                fontSize: 'var(--font-size-xs)',
                                                                                background: 'rgba(139, 92, 246, 0.1)',
                                                                                color: '#8b5cf6',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 'var(--radius-sm)'
                                                                            }}>after selection</span>
                                                                        </div>
                                                                        <pre style={{
                                                                            background: 'var(--color-surface)',
                                                                            border: '1px solid var(--color-border)',
                                                                            borderRadius: 'var(--radius-md)',
                                                                            padding: 'var(--space-3)',
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            fontFamily: 'monospace',
                                                                            color: 'var(--color-text-secondary)',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word',
                                                                            margin: 0,
                                                                            maxHeight: '250px',
                                                                            overflow: 'auto'
                                                                        }}>
{`You are a film director preparing the opening shot. Create a detailed image prompt combining the selected main character and location into one cohesive scene.

SELECTED MAIN CHARACTER:
- Title: {selectedCasting.title}
- Description: {selectedCasting.description}

SELECTED LOCATION:
- Title: {selectedLocation.title}
- Description: {selectedLocation.description}

VIDEO CONTEXT:
- Product: {product}
- Marketing Angle: {marketingAngle}
- Visual Style: {visualStyle}
- Tone: {tone}
- Video Style: {videoStyle}

The image should:
1. Show the main character naturally placed in the location
2. Capture the mood and tone of the advertisement
3. Set up the visual story for the video
4. Be suitable as a style reference for video generation

Output: Detailed image prompt describing character positioning, location details, lighting, camera angle, and overall mood.`}
                                                                        </pre>
                                                                        <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                            {['selectedCasting', 'selectedLocation', 'product', 'visualStyle', 'tone', 'videoStyle'].map(v => (
                                                                                <code key={v} style={{
                                                                                    background: 'var(--color-surface)',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: 'var(--radius-sm)',
                                                                                    fontFamily: 'monospace',
                                                                                    fontSize: '10px',
                                                                                    color: '#8b5cf6'
                                                                                }}>{v}</code>
                                                                            ))}
                                                                        </div>
                                                                    </div>

                                                                    {/* Product Scene Prompt - combines combined scene with product images */}
                                                                    <div style={{
                                                                        marginTop: 'var(--space-3)',
                                                                        paddingTop: 'var(--space-3)',
                                                                        borderTop: '1px dashed var(--color-border)'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: '#f59e0b' }}>📦 Product Scene Prompt</span>
                                                                            <span style={{
                                                                                fontSize: 'var(--font-size-xs)',
                                                                                background: 'rgba(245, 158, 11, 0.1)',
                                                                                color: '#f59e0b',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 'var(--radius-sm)'
                                                                            }}>after combined scene</span>
                                                                        </div>
                                                                        <pre style={{
                                                                            background: 'var(--color-surface)',
                                                                            border: '1px solid var(--color-border)',
                                                                            borderRadius: 'var(--radius-md)',
                                                                            padding: 'var(--space-3)',
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            fontFamily: 'monospace',
                                                                            color: 'var(--color-text-secondary)',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word',
                                                                            margin: 0,
                                                                            maxHeight: '250px',
                                                                            overflow: 'auto'
                                                                        }}>
{`You are a film director creating the hero shot for a video advertisement. Create a detailed image prompt that integrates the PRODUCT into the scene with the main character and location.

COMBINED SCENE (Main Character + Location):
{combinedSceneImage.description}

PRODUCT TO INTEGRATE:
- Product Name: {product}
- Product Description: {productDescription}
- Marketing Angle: {marketingAngle}

PRODUCT REFERENCE IMAGES:
{productImages} (from assets.productImages)

VIDEO CONTEXT:
- Visual Style: {visualStyle}
- Tone: {tone}
- Video Style: {videoStyle}
- Desired Emotion: {desiredEmotion}

IMPORTANT REQUIREMENTS:
1. The product must be prominently featured and clearly visible
2. The main character should be interacting with or presenting the product naturally
3. Maintain the established location and atmosphere from the combined scene
4. The product placement should feel authentic, not forced
5. This image will be used as the reference for the video's opening frame

Output: Detailed image prompt describing product positioning, character interaction with product, scene composition, and lighting.`}
                                                                        </pre>
                                                                        <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                            {['combinedSceneImage', 'product', 'productDescription', 'marketingAngle', 'productImages', 'visualStyle', 'tone', 'desiredEmotion'].map(v => (
                                                                                <code key={v} style={{
                                                                                    background: 'var(--color-surface)',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: 'var(--radius-sm)',
                                                                                    fontFamily: 'monospace',
                                                                                    fontSize: '10px',
                                                                                    color: '#f59e0b'
                                                                                }}>{v}</code>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* First Frame Generation Prompt - inline with step */}
                                                            {step.field === 'videoSettings.firstFrame' && (
                                                                <div style={{
                                                                    background: 'rgba(245, 158, 11, 0.05)',
                                                                    padding: 'var(--space-3)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    border: '1px solid rgba(245, 158, 11, 0.2)'
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: 'var(--color-warning)' }}>Image Generation Prompt</span>
                                                                        <span style={{
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            background: 'rgba(124, 58, 237, 0.1)',
                                                                            color: 'var(--color-primary)',
                                                                            padding: '2px 6px',
                                                                            borderRadius: 'var(--radius-sm)'
                                                                        }}>from code</span>
                                                                    </div>
                                                                    <pre style={{
                                                                        background: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderRadius: 'var(--radius-md)',
                                                                        padding: 'var(--space-3)',
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontFamily: 'monospace',
                                                                        color: 'var(--color-text-secondary)',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        margin: 0,
                                                                        maxHeight: '300px',
                                                                        overflow: 'auto'
                                                                    }}>
{`You are a cinematographer creating the opening frame for a video advertisement. Generate 4 DISTINCT first frame options based on the script and creative direction.

SCRIPT: {script}

VIDEO CONTEXT:
- Product: {product}
- Video Style: {videoStyle}
- Visual Concept: {visualConcept}
- Target Audience: {targetAudience}
- Desired Emotion: {desiredEmotion}
- Selected Character: {selectedCasting.title} - {selectedCasting.description}
- Selected Location: {selectedLocation.title} - {selectedLocation.description}

{feedback ? "USER FEEDBACK: {feedback} - Generate NEW options addressing this." : ""}

REQUIREMENTS - Each option should:
1. Capture a different moment or angle from the script's opening
2. Set the tone and mood for the entire video
3. Be visually compelling and attention-grabbing
4. Work as a standalone image that could hook viewers
5. Maintain consistency with any selected casting/location

OUTPUT FORMAT:
[{ id, title, description, imagePrompt }, ...]`}
                                                                    </pre>
                                                                    <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                        {['script', 'product', 'videoStyle', 'visualConcept', 'targetAudience', 'desiredEmotion', 'selectedCasting', 'selectedLocation'].map(v => (
                                                                            <code key={v} style={{
                                                                                background: 'var(--color-surface)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 'var(--radius-sm)',
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '10px',
                                                                                color: 'var(--color-primary)'
                                                                            }}>{v}</code>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Last Frame Generation Prompt - inline with step */}
                                                            {step.field === 'videoSettings.lastFrame' && (
                                                                <div style={{
                                                                    background: 'rgba(245, 158, 11, 0.05)',
                                                                    padding: 'var(--space-3)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    border: '1px solid rgba(245, 158, 11, 0.2)'
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: 'var(--color-warning)' }}>Image Generation Prompt</span>
                                                                        <span style={{
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            background: 'rgba(124, 58, 237, 0.1)',
                                                                            color: 'var(--color-primary)',
                                                                            padding: '2px 6px',
                                                                            borderRadius: 'var(--radius-sm)'
                                                                        }}>from code</span>
                                                                    </div>
                                                                    <pre style={{
                                                                        background: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderRadius: 'var(--radius-md)',
                                                                        padding: 'var(--space-3)',
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontFamily: 'monospace',
                                                                        color: 'var(--color-text-secondary)',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        margin: 0,
                                                                        maxHeight: '300px',
                                                                        overflow: 'auto'
                                                                    }}>
{`You are a cinematographer creating the closing frame for a video advertisement. Generate 4 DISTINCT last frame options that maintain VISUAL CONTINUITY with the first frame.

FIRST FRAME REFERENCE (MUST MAINTAIN CONTINUITY):
- Description: {selectedFirstFrame.description}
- Visual Elements: {selectedFirstFrame.prompt}

SCRIPT: {script}

TEXT ELEMENTS TO INCLUDE:
- Headline: "{headline}"
- Call-to-Action: "{cta}"
- Brand Name: "{brandName}"

BRAND COLORS: {brandColors}
VIDEO STYLE: {videoStyle}

CRITICAL CONTINUITY REQUIREMENTS:
1. Same characters/subjects as the first frame
2. Same location/setting as the first frame
3. Consistent lighting style and color grading
4. Natural visual progression from opening to closing

TEXT OVERLAY REQUIREMENTS:
1. Headline prominently displayed and readable
2. CTA as button or distinct element
3. Brand logo visible but not overwhelming
4. Text placement should not obstruct key visuals

OUTPUT FORMAT:
[{ id, title, description, imagePrompt, textElements: { headline, cta, logo } }, ...]`}
                                                                    </pre>
                                                                    <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                        {['script', 'videoStyle', 'selectedFirstFrame', 'headline', 'cta', 'brandName', 'brandColors'].map(v => (
                                                                            <code key={v} style={{
                                                                                background: 'var(--color-surface)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 'var(--radius-sm)',
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '10px',
                                                                                color: 'var(--color-primary)'
                                                                            }}>{v}</code>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                <button
                                                    onClick={() => addStep(phase.id)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 'var(--space-2)',
                                                        padding: 'var(--space-3)',
                                                        background: 'transparent',
                                                        border: '1px dashed var(--color-border)',
                                                        borderRadius: 'var(--radius-lg)',
                                                        color: 'var(--color-text-secondary)',
                                                        cursor: 'pointer',
                                                        transition: 'all var(--transition-fast)',
                                                        width: '100%'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.background = 'var(--color-surface)';
                                                        e.currentTarget.style.borderColor = 'var(--color-primary)';
                                                        e.currentTarget.style.color = 'var(--color-primary)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = 'transparent';
                                                        e.currentTarget.style.borderColor = 'var(--color-border)';
                                                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                                                    }}
                                                >
                                                    <Plus size={20} />
                                                    <span>Add Step</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'phases' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        {/* Phases Legend */}
                        <div style={{
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-4)',
                            border: '1px solid var(--color-border)',
                            display: 'flex',
                            gap: 'var(--space-6)',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: '2px solid #7c3aed' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Phase</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: '2px solid #2563eb' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>User Prompt</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, #10b981, #059669)', border: '2px solid #059669' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>AI Processing</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: '2px solid #d97706' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Decision</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, #ec4899, #db2777)', border: '2px solid #db2777' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Conditional</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, #10b981, #059669)', border: '3px solid #059669', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Milestone/Output</span>
                            </div>
                        </div>

                        {/* Phase Order - Drag to Reorder */}
                        {config?.stepSequencing?.phaseOrder && (
                            <div style={{
                                background: 'var(--color-surface)',
                                borderRadius: 'var(--radius-lg)',
                                padding: 'var(--space-4)',
                                border: '1px solid var(--color-border)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                                    <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                        Phase Order
                                    </h4>
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                        Drag to reorder phases
                                    </span>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                    {config.stepSequencing.phaseOrder.map((phaseId, idx) => {
                                        const isConditional = config.stepSequencing.conditionalPhases?.[phaseId];
                                        const isDragOverThis = dragOverPhase === idx;
                                        const isDraggingThis = draggedPhase === idx;

                                        return (
                                            <div
                                                key={phaseId}
                                                draggable
                                                onDragStart={(e) => handlePhaseDragStart(e, idx)}
                                                onDragOver={(e) => handlePhaseDragOver(e, idx)}
                                                onDragLeave={handlePhaseDragLeave}
                                                onDrop={(e) => handlePhaseDrop(e, idx)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-2)',
                                                    background: isDragOverThis
                                                        ? 'rgba(59, 130, 246, 0.2)'
                                                        : isConditional
                                                            ? 'rgba(236, 72, 153, 0.1)'
                                                            : 'var(--color-primary-light)',
                                                    color: isConditional ? '#ec4899' : 'var(--color-primary)',
                                                    padding: 'var(--space-1) var(--space-3)',
                                                    borderRadius: 'var(--radius-full)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 500,
                                                    cursor: 'grab',
                                                    opacity: isDraggingThis ? 0.5 : 1,
                                                    border: isDragOverThis ? '2px dashed #3b82f6' : '1px solid transparent',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <GripVertical size={12} />
                                                <span>{idx + 1}.</span>
                                                <span>{phaseId.replace(/_/g, ' ')}</span>
                                                {isConditional && (
                                                    <span style={{ fontSize: '10px', opacity: 0.7 }}>(conditional)</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Conditional phases info */}
                                {config.stepSequencing.conditionalPhases && Object.keys(config.stepSequencing.conditionalPhases).length > 0 && (
                                    <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                                            Conditional Phases:
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                            {Object.entries(config.stepSequencing.conditionalPhases).map(([phaseId, phaseConfig]) => (
                                                <div key={phaseId} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-xs)' }}>
                                                    <span style={{ fontWeight: 500, color: '#ec4899' }}>{phaseId}</span>
                                                    <ArrowRight size={12} style={{ color: 'var(--color-text-tertiary)' }} />
                                                    <code style={{ background: 'var(--color-bg-primary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                                                        {phaseConfig.condition}
                                                    </code>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Flowchart Container */}
                        <div style={{
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-xl)',
                            padding: 'var(--space-6)',
                            border: '1px solid var(--color-border)',
                            minHeight: 'calc(100vh - 300px)',
                            overflow: 'auto'
                        }}>
                            {/* Documentation Section */}
                            {config._documentation && (
                                <div style={{ marginBottom: 'var(--space-8)' }}>
                                    <div style={{
                                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05))',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        marginBottom: 'var(--space-4)'
                                    }}>
                                        <h3 style={{ margin: 0, marginBottom: 'var(--space-2)', color: '#8b5cf6', fontSize: 'var(--font-size-lg)' }}>📚 Documentation & Metadata</h3>
                                        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                                            Version: {config.metadata?.version} | AI Model: {config.metadata?.ai_model}
                                        </p>
                                    </div>

                                    {/* Phase Flow Overview */}
                                    {config._documentation._phaseFlow && (
                                        <div style={{
                                            background: 'var(--color-surface)',
                                            borderRadius: 'var(--radius-lg)',
                                            padding: 'var(--space-4)',
                                            border: '1px solid var(--color-border)',
                                            marginBottom: 'var(--space-4)'
                                        }}>
                                            <h4 style={{ margin: 0, marginBottom: 'var(--space-3)', color: 'var(--color-text-primary)' }}>🔄 Phase Flow Overview</h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                {config._documentation._phaseFlow.map((step, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 'var(--space-2)',
                                                        padding: 'var(--space-2)',
                                                        background: 'var(--color-bg-primary)',
                                                        borderRadius: 'var(--radius-md)',
                                                        fontSize: 'var(--font-size-sm)'
                                                    }}>
                                                        <code style={{ color: 'var(--color-primary)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{step.split(':')[0]}:</code>
                                                        <span style={{ color: 'var(--color-text-secondary)' }}>{step.split(':').slice(1).join(':')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Global Rules Section */}
                            {config.globalRules && (
                                <div style={{ marginBottom: 'var(--space-8)' }}>
                                    <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>⚙️ Global Rules</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-4)' }}>
                                        {Object.entries(config.globalRules).map(([key, rule]) => (
                                            <div key={key} style={{
                                                background: 'var(--color-surface)',
                                                borderRadius: 'var(--radius-lg)',
                                                padding: 'var(--space-4)',
                                                border: '1px solid var(--color-border)'
                                            }}>
                                                <h4 style={{ margin: 0, marginBottom: 'var(--space-2)', color: '#f59e0b', fontSize: 'var(--font-size-sm)', textTransform: 'capitalize' }}>
                                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                                </h4>
                                                {typeof rule === 'object' && rule !== null ? (
                                                    <>
                                                        {rule.description && (
                                                            <p style={{ margin: 0, marginBottom: 'var(--space-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>
                                                                {rule.description}
                                                            </p>
                                                        )}
                                                        {rule.steps && Array.isArray(rule.steps) && (
                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                {rule.steps.map((step, i) => (
                                                                    <div key={i}>• {typeof step === 'string' ? step : JSON.stringify(step)}</div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {rule.rules && Array.isArray(rule.rules) && (
                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                {rule.rules.map((r, i) => (
                                                                    <div key={i}>• {typeof r === 'string' ? r : JSON.stringify(r)}</div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {!rule.description && !rule.steps && !rule.rules && (
                                                            <pre style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', whiteSpace: 'pre-wrap' }}>
                                                                {JSON.stringify(rule, null, 2)}
                                                            </pre>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>
                                                        {String(rule)}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Conversational Philosophy Section */}
                            {config.conversationalPhilosophy && (
                                <div style={{ marginBottom: 'var(--space-8)' }}>
                                    <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>💬 Conversational Philosophy</h3>
                                    <div style={{
                                        background: 'var(--color-surface)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        border: '1px solid var(--color-border)',
                                        marginBottom: 'var(--space-4)'
                                    }}>
                                        <div style={{ fontWeight: 600, color: 'var(--color-primary)', marginBottom: 'var(--space-2)' }}>Core Principle</div>
                                        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                                            {config.conversationalPhilosophy.corePrinciple}
                                        </p>
                                    </div>

                                    {/* Expert Personalities */}
                                    {config.conversationalPhilosophy.expertPersonalities && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                                            {Object.entries(config.conversationalPhilosophy.expertPersonalities).map(([key, expert]) => (
                                                <div key={key} style={{
                                                    background: 'var(--color-surface)',
                                                    borderRadius: 'var(--radius-md)',
                                                    padding: 'var(--space-3)',
                                                    border: '1px solid var(--color-border)'
                                                }}>
                                                    <div style={{ fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 'var(--space-1)' }}>{expert.name}</div>
                                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', marginBottom: 'var(--space-1)' }}>{expert.role}</div>
                                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>{expert.personality}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Main Phase Flowchart */}
                            <div style={{ marginBottom: 'var(--space-8)' }}>
                                <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>🎯 Conversation Flow</h3>

                                {/* Entry Point */}
                                {config.conversationFlow?.entryPoint && (
                                    <div style={{ marginBottom: 'var(--space-6)' }}>
                                        <div
                                            onClick={() => toggleFlowchartPhase('entryPoint')}
                                            style={{
                                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                                borderRadius: expandedFlowchartPhases['entryPoint'] ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)',
                                                padding: 'var(--space-4)',
                                                color: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                    {expandedFlowchartPhases['entryPoint'] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                                    <span style={{ fontSize: 'var(--font-size-xl)' }}>👋</span>
                                                    <span style={{ fontWeight: 'bold' }}>Entry Point</span>
                                                </div>
                                                {config.conversationFlow.entryPoint.options && (
                                                    <span style={{
                                                        background: 'rgba(255,255,255,0.2)',
                                                        padding: '2px 8px',
                                                        borderRadius: 'var(--radius-sm)',
                                                        fontSize: 'var(--font-size-xs)'
                                                    }}>
                                                        {config.conversationFlow.entryPoint.options.length} options
                                                    </span>
                                                )}
                                            </div>
                                            <p style={{ margin: 0, opacity: 0.9, fontSize: 'var(--font-size-sm)' }}>
                                                {config.conversationFlow.entryPoint.message?.substring(0, 100)}...
                                            </p>
                                        </div>

                                        {/* Expanded Entry Point Content */}
                                        {expandedFlowchartPhases['entryPoint'] && (
                                            <div style={{
                                                background: 'var(--color-surface)',
                                                borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
                                                padding: 'var(--space-4)',
                                                border: '1px solid var(--color-border)',
                                                borderTop: 'none',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 'var(--space-4)'
                                            }}>
                                                {/* Greeting Message */}
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Greeting Message</label>
                                                    <textarea
                                                        value={config.conversationFlow.entryPoint?.message || ''}
                                                        onChange={(e) => {
                                                            setConfig(prev => ({
                                                                ...prev,
                                                                conversationFlow: {
                                                                    ...prev.conversationFlow,
                                                                    entryPoint: {
                                                                        ...prev.conversationFlow.entryPoint,
                                                                        message: e.target.value
                                                                    }
                                                                }
                                                            }));
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            background: 'var(--color-bg-primary)',
                                                            border: '1px solid var(--color-border)',
                                                            borderRadius: 'var(--radius-md)',
                                                            padding: 'var(--space-2)',
                                                            fontSize: 'var(--font-size-sm)',
                                                            color: 'var(--color-text-primary)',
                                                            outline: 'none',
                                                            minHeight: '80px'
                                                        }}
                                                    />
                                                </div>

                                                {/* Entry Point Options */}
                                                {config.conversationFlow.entryPoint?.options && (
                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Options ({config.conversationFlow.entryPoint.options.length})</label>
                                                            <button onClick={addEntryPointOption} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>+ Add Option</button>
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                            {config.conversationFlow.entryPoint.options.map((opt, optIdx) => (
                                                                <div key={optIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                                                                    <input
                                                                        type="text"
                                                                        value={opt.text}
                                                                        onChange={(e) => updateEntryPointOption(optIdx, 'text', e.target.value)}
                                                                        placeholder="Text"
                                                                        style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={opt.value}
                                                                        onChange={(e) => updateEntryPointOption(optIdx, 'value', e.target.value)}
                                                                        placeholder="Value"
                                                                        style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                                                    />
                                                                    <button onClick={() => removeEntryPointOption(optIdx)} style={{ background: 'transparent', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Connector Line */}
                                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-2)' }}>
                                            <div style={{ width: 2, height: 30, background: 'var(--color-border)' }} />
                                        </div>
                                    </div>
                                )}

                                {/* Phases Flow */}
                                {config.phases && Object.entries(config.phases).map(([phaseKey, phase], phaseIndex) => {
                                    const phaseFlowKey = getPhaseKey(phase.id);
                                    const phaseFlow = config.conversationFlow?.[phaseFlowKey];
                                    const isConditional = phase.condition;
                                    const isPhaseExpanded = expandedFlowchartPhases[phase.id];

                                    return (
                                        <div key={phaseKey} style={{ marginBottom: 'var(--space-6)' }}>
                                            {/* Phase Header - Clickable */}
                                            <div
                                                onClick={() => toggleFlowchartPhase(phase.id)}
                                                style={{
                                                    background: isConditional
                                                        ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.9), rgba(219, 39, 119, 0.9))'
                                                        : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                                    borderRadius: isPhaseExpanded ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)',
                                                    padding: 'var(--space-4)',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                        {isPhaseExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                                        <span style={{ fontSize: 'var(--font-size-xl)' }}>{phase.emoji}</span>
                                                        <span style={{ fontWeight: 'bold' }}>{phase.title}</span>
                                                        {phase.persona && <span style={{ opacity: 0.8, fontSize: 'var(--font-size-sm)' }}>({phase.persona.name})</span>}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                        {phaseFlow?.steps && (
                                                            <span style={{
                                                                background: 'rgba(255,255,255,0.2)',
                                                                padding: '2px 8px',
                                                                borderRadius: 'var(--radius-sm)',
                                                                fontSize: 'var(--font-size-xs)'
                                                            }}>
                                                                {phaseFlow.steps.length} steps
                                                            </span>
                                                        )}
                                                        {isConditional && (
                                                            <span style={{
                                                                background: 'rgba(255,255,255,0.2)',
                                                                padding: '2px 8px',
                                                                borderRadius: 'var(--radius-sm)',
                                                                fontSize: 'var(--font-size-xs)'
                                                            }}>
                                                                Conditional
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <p style={{ margin: 0, opacity: 0.9, fontSize: 'var(--font-size-sm)' }}>{phase.subtitle}</p>
                                                {isConditional && (
                                                    <div style={{
                                                        marginTop: 'var(--space-2)',
                                                        padding: 'var(--space-2)',
                                                        background: 'rgba(0,0,0,0.2)',
                                                        borderRadius: 'var(--radius-sm)',
                                                        fontSize: 'var(--font-size-xs)',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        if ({phase.condition})
                                                    </div>
                                                )}
                                            </div>

                                            {/* Expanded Phase Content */}
                                            {isPhaseExpanded && (
                                                <div style={{
                                                    background: 'var(--color-surface)',
                                                    borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
                                                    padding: 'var(--space-4)',
                                                    border: '1px solid var(--color-border)',
                                                    borderTop: 'none'
                                                }}>
                                                    {/* Phase Settings */}
                                                    <div style={{ marginBottom: 'var(--space-4)' }}>
                                                        <h4 style={{ margin: 0, marginBottom: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Phase Settings</h4>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Title</label>
                                                                <input
                                                                    type="text"
                                                                    value={phase.title || ''}
                                                                    onChange={(e) => updatePhase(phase.id, 'title', e.target.value)}
                                                                    style={{ width: '100%', background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Subtitle</label>
                                                                <input
                                                                    type="text"
                                                                    value={phase.subtitle || ''}
                                                                    onChange={(e) => updatePhase(phase.id, 'subtitle', e.target.value)}
                                                                    style={{ width: '100%', background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}
                                                                />
                                                            </div>
                                                        </div>
                                                        {phase.persona && (
                                                            <div style={{ marginTop: 'var(--space-3)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                                                <div>
                                                                    <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Persona Name</label>
                                                                    <input
                                                                        type="text"
                                                                        value={phase.persona.name || ''}
                                                                        onChange={(e) => updatePhasePersona(phase.id, 'name', e.target.value)}
                                                                        style={{ width: '100%', background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Tone</label>
                                                                    <input
                                                                        type="text"
                                                                        value={phase.persona.tone || ''}
                                                                        onChange={(e) => updatePhasePersona(phase.id, 'tone', e.target.value)}
                                                                        style={{ width: '100%', background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {phaseFlow?.steps && phaseFlow.steps.filter(s => s.requiredField).length > 0 && (
                                                            <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                Required Fields: {phaseFlow.steps.filter(s => s.requiredField).map(s => s.field).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Phase Steps */}
                                                    {phaseFlow?.steps && phaseFlow.steps.length > 0 && (
                                                        <div>
                                                            <h4 style={{ margin: 0, marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                                Conversation Steps ({phaseFlow.steps.length})
                                                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
                                                                    - Drag to reorder
                                                                </span>
                                                            </h4>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                                                {phaseFlow.steps.map((step, stepIndex) => {
                                                                    const isAIGenerated = step.optionsGeneration?.source === 'AI_GENERATED';
                                                                    const hasCondition = step.condition;
                                                                    const stepKey = `${phase.id}-${stepIndex}`;
                                                                    const isStepExpanded = expandedFlowchartSteps[stepKey];
                                                                    const isStepFlowEditorOpen = showStepFlowEditor[stepKey];
                                                                    const stepFlow = config?.stepSequencing?.stepFlows?.[phase.id]?.[stepIndex.toString()];
                                                                    const isDragOver = dragOverStep?.phaseId === phase.id && dragOverStep?.stepIndex === stepIndex;
                                                                    const isDragging = draggedStep?.phaseId === phase.id && draggedStep?.stepIndex === stepIndex;

                                                                    return (
                                                                        <div
                                                                            key={stepIndex}
                                                                            draggable
                                                                            onDragStart={(e) => handleStepDragStart(e, phase.id, stepIndex)}
                                                                            onDragOver={(e) => handleStepDragOver(e, phase.id, stepIndex)}
                                                                            onDragLeave={handleStepDragLeave}
                                                                            onDrop={(e) => handleStepDrop(e, phase.id, stepIndex)}
                                                                            onDragEnd={handleStepDragEnd}
                                                                            style={{
                                                                                background: isDragOver ? 'rgba(59, 130, 246, 0.1)' : 'var(--color-bg-primary)',
                                                                                borderRadius: 'var(--radius-md)',
                                                                                border: isDragOver
                                                                                    ? '2px dashed #3b82f6'
                                                                                    : `1px solid ${hasCondition ? 'rgba(245, 158, 11, 0.3)' : (isAIGenerated ? 'rgba(16, 185, 129, 0.3)' : 'var(--color-border)')}`,
                                                                                opacity: isDragging ? 0.5 : 1,
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                        >
                                                                            {/* Step Header - Clickable */}
                                                                            <div
                                                                                style={{
                                                                                    padding: 'var(--space-3)',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between'
                                                                                }}
                                                                            >
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                                                    {/* Drag Handle */}
                                                                                    <div style={{ cursor: 'grab', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center' }}>
                                                                                        <GripVertical size={14} />
                                                                                    </div>
                                                                                    <div onClick={() => toggleFlowchartStep(phase.id, stepIndex)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                                                        {isStepExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                                                    </div>
                                                                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Step {stepIndex + 1}</span>
                                                                                    <code style={{
                                                                                        fontSize: 'var(--font-size-xs)',
                                                                                        background: 'var(--color-surface)',
                                                                                        padding: '2px 6px',
                                                                                        borderRadius: 'var(--radius-sm)',
                                                                                        color: 'var(--color-primary)'
                                                                                    }}>
                                                                                        {step.field}
                                                                                    </code>
                                                                                    {isAIGenerated && (
                                                                                        <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>AI</span>
                                                                                    )}
                                                                                    {hasCondition && (
                                                                                        <span style={{ fontSize: '10px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>Conditional</span>
                                                                                    )}
                                                                                    {/* Required Field Toggle */}
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            updateStepField(phase.id, stepIndex, 'requiredField', !step.requiredField);
                                                                                        }}
                                                                                        style={{
                                                                                            fontSize: '10px',
                                                                                            background: step.requiredField ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 100, 100, 0.1)',
                                                                                            color: step.requiredField ? '#ef4444' : 'var(--color-text-tertiary)',
                                                                                            padding: '2px 6px',
                                                                                            borderRadius: 'var(--radius-sm)',
                                                                                            border: 'none',
                                                                                            cursor: 'pointer',
                                                                                            fontWeight: step.requiredField ? 500 : 400
                                                                                        }}
                                                                                        title={step.requiredField ? 'Click to make optional' : 'Click to make required'}
                                                                                    >
                                                                                        {step.requiredField ? '* Required' : 'Optional'}
                                                                                    </button>
                                                                                    {/* Step Flow indicator */}
                                                                                    {stepFlow?.onComplete?.handler && (
                                                                                        <span style={{ fontSize: '10px', background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', padding: '2px 6px', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                                                            <Zap size={10} />
                                                                                            {stepFlow.onComplete.handler.replace(/_/g, ' ').toLowerCase().split(' ').slice(0, 2).join(' ')}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                                                    {/* Next step indicator */}
                                                                                    {stepFlow?.nextStep !== undefined && (
                                                                                        <span style={{
                                                                                            fontSize: 'var(--font-size-xs)',
                                                                                            color: stepFlow.nextStep === 'phase_complete' ? '#10b981' : 'var(--color-text-tertiary)',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '2px'
                                                                                        }}>
                                                                                            <ArrowRight size={12} />
                                                                                            {stepFlow.nextStep === 'phase_complete' ? 'Done' : `Step ${stepFlow.nextStep + 1}`}
                                                                                        </span>
                                                                                    )}
                                                                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                                        {step.options?.length || 0} options
                                                                                    </span>
                                                                                    {/* Step Flow Editor Toggle */}
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); toggleStepFlowEditor(phase.id, stepIndex); }}
                                                                                        style={{
                                                                                            background: isStepFlowEditorOpen ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                                                                                            border: '1px solid',
                                                                                            borderColor: isStepFlowEditorOpen ? '#8b5cf6' : 'var(--color-border)',
                                                                                            borderRadius: 'var(--radius-sm)',
                                                                                            padding: '2px 6px',
                                                                                            cursor: 'pointer',
                                                                                            fontSize: 'var(--font-size-xs)',
                                                                                            color: isStepFlowEditorOpen ? '#8b5cf6' : 'var(--color-text-tertiary)',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '4px'
                                                                                        }}
                                                                                        title="Edit step flow"
                                                                                    >
                                                                                        <Zap size={12} />
                                                                                        Flow
                                                                                    </button>
                                                                                </div>
                                                                            </div>

                                                                            {/* Step Flow Editor - Inline */}
                                                                            {isStepFlowEditorOpen && (
                                                                                <div style={{
                                                                                    padding: 'var(--space-3)',
                                                                                    borderTop: '1px solid var(--color-border)',
                                                                                    background: 'rgba(139, 92, 246, 0.03)'
                                                                                }}>
                                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
                                                                                        {/* Handler */}
                                                                                        <div>
                                                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#8b5cf6', marginBottom: 'var(--space-1)', fontWeight: 500 }}>
                                                                                                onComplete Handler
                                                                                            </label>
                                                                                            <select
                                                                                                value={stepFlow?.onComplete?.handler || ''}
                                                                                                onChange={(e) => updateStepFlowHandler(phase.id, stepIndex, e.target.value)}
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    background: 'var(--color-surface)',
                                                                                                    border: '1px solid var(--color-border)',
                                                                                                    borderRadius: 'var(--radius-sm)',
                                                                                                    padding: 'var(--space-2)',
                                                                                                    fontSize: 'var(--font-size-xs)',
                                                                                                    color: 'var(--color-text-primary)'
                                                                                                }}
                                                                                            >
                                                                                                <option value="">Select handler...</option>
                                                                                                {availableHandlers.map(h => (
                                                                                                    <option key={h} value={h}>{h}</option>
                                                                                                ))}
                                                                                            </select>
                                                                                        </div>
                                                                                        {/* Target Field */}
                                                                                        <div>
                                                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#8b5cf6', marginBottom: 'var(--space-1)', fontWeight: 500 }}>
                                                                                                Target Field
                                                                                            </label>
                                                                                            <input
                                                                                                type="text"
                                                                                                value={stepFlow?.onComplete?.targetField || ''}
                                                                                                onChange={(e) => updateStepFlowTargetField(phase.id, stepIndex, e.target.value)}
                                                                                                placeholder="e.g., targetAudience"
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    background: 'var(--color-surface)',
                                                                                                    border: '1px solid var(--color-border)',
                                                                                                    borderRadius: 'var(--radius-sm)',
                                                                                                    padding: 'var(--space-2)',
                                                                                                    fontSize: 'var(--font-size-xs)',
                                                                                                    color: 'var(--color-text-primary)',
                                                                                                    fontFamily: 'monospace'
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                        {/* Next Step */}
                                                                                        <div>
                                                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#8b5cf6', marginBottom: 'var(--space-1)', fontWeight: 500 }}>
                                                                                                Next Step
                                                                                            </label>
                                                                                            <select
                                                                                                value={stepFlow?.nextStep === 'phase_complete' ? 'phase_complete' : (stepFlow?.nextStep ?? '')}
                                                                                                onChange={(e) => updateStepFlowNextStep(phase.id, stepIndex, e.target.value)}
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    background: 'var(--color-surface)',
                                                                                                    border: '1px solid var(--color-border)',
                                                                                                    borderRadius: 'var(--radius-sm)',
                                                                                                    padding: 'var(--space-2)',
                                                                                                    fontSize: 'var(--font-size-xs)',
                                                                                                    color: 'var(--color-text-primary)'
                                                                                                }}
                                                                                            >
                                                                                                <option value="">Default (next)</option>
                                                                                                {phaseFlow.steps.map((_, idx) => (
                                                                                                    <option key={idx} value={idx}>Step {idx + 1}</option>
                                                                                                ))}
                                                                                                <option value="phase_complete">Phase Complete</option>
                                                                                            </select>
                                                                                        </div>
                                                                                    </div>
                                                                                    {/* Step Condition */}
                                                                                    <div style={{ marginTop: 'var(--space-3)' }}>
                                                                                        <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#8b5cf6', marginBottom: 'var(--space-1)', fontWeight: 500 }}>
                                                                                            Skip Condition (step shows only if condition is true)
                                                                                        </label>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={stepFlow?.condition || ''}
                                                                                            onChange={(e) => updateStepFlowCondition(phase.id, stepIndex, e.target.value)}
                                                                                            placeholder="e.g., outputType === 'video'"
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                background: 'var(--color-surface)',
                                                                                                border: '1px solid var(--color-border)',
                                                                                                borderRadius: 'var(--radius-sm)',
                                                                                                padding: 'var(--space-2)',
                                                                                                fontSize: 'var(--font-size-xs)',
                                                                                                color: 'var(--color-text-primary)',
                                                                                                fontFamily: 'monospace'
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* Expanded Step Content */}
                                                                            {isStepExpanded && (
                                                                                <div style={{
                                                                                    padding: 'var(--space-3)',
                                                                                    borderTop: '1px solid var(--color-border)',
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    gap: 'var(--space-3)'
                                                                                }}>
                                                                                    {/* Field Name */}
                                                                                    <div>
                                                                                        <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>Field Name</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={step.field || ''}
                                                                                            onChange={(e) => updateStepField(phase.id, stepIndex, 'field', e.target.value)}
                                                                                            style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', fontFamily: 'monospace' }}
                                                                                        />
                                                                                    </div>

                                                                                    {/* Condition */}
                                                                                    {hasCondition && (
                                                                                        <div style={{ padding: 'var(--space-2)', background: 'rgba(245, 158, 11, 0.1)', borderRadius: 'var(--radius-md)' }}>
                                                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#f59e0b', marginBottom: 'var(--space-1)' }}>Condition</label>
                                                                                            <code style={{ fontSize: 'var(--font-size-xs)', color: '#f59e0b' }}>{step.condition}</code>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Message */}
                                                                                    <div>
                                                                                        <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>
                                                                                            {step.audienceConfirmationFlows ? 'Confirmation Messages (Dynamic)' : 'Message'}
                                                                                        </label>
                                                                                        {step.audienceConfirmationFlows ? (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                                <div>
                                                                                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>URL Inference Message</span>
                                                                                                    <textarea
                                                                                                        value={step.audienceConfirmationFlows.urlInferenceFlow?.confirmationMessage || ''}
                                                                                                        onChange={(e) => updateAudienceMessage(phase.id, stepIndex, 'urlInferenceFlow', e.target.value)}
                                                                                                        style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', minHeight: '60px' }}
                                                                                                    />
                                                                                                </div>
                                                                                                <div>
                                                                                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Brand Audience Message</span>
                                                                                                    <textarea
                                                                                                        value={step.audienceConfirmationFlows.brandAudienceFlow?.confirmationMessage || ''}
                                                                                                        onChange={(e) => updateAudienceMessage(phase.id, stepIndex, 'brandAudienceFlow', e.target.value)}
                                                                                                        style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', minHeight: '60px' }}
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <textarea
                                                                                                value={typeof step.message === 'string' ? step.message : (step.greeting?.message || '')}
                                                                                                onChange={(e) => updateStepGreeting(phase.id, stepIndex, e.target.value)}
                                                                                                style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', minHeight: '80px' }}
                                                                                            />
                                                                                        )}
                                                                                    </div>

                                                                                    {/* Options */}
                                                                                    {step.options && Array.isArray(step.options) && (
                                                                                        <div>
                                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                                                                                <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Options ({step.options.length})</label>
                                                                                                <button onClick={() => addStepOption(phase.id, stepIndex, 'options')} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>+ Add</button>
                                                                                            </div>
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                                {step.options.map((opt, optIdx) => (
                                                                                                    <div key={optIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={typeof opt?.text === 'string' ? opt.text : ''}
                                                                                                            onChange={(e) => updateStepOption(phase.id, stepIndex, optIdx, 'text', e.target.value, 'options')}
                                                                                                            placeholder="Text"
                                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}
                                                                                                        />
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={typeof opt?.value === 'string' ? opt.value : ''}
                                                                                                            onChange={(e) => updateStepOption(phase.id, stepIndex, optIdx, 'value', e.target.value, 'options')}
                                                                                                            placeholder="Value"
                                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                                                                                        />
                                                                                                        <button onClick={() => removeStepOption(phase.id, stepIndex, optIdx, 'options')} style={{ background: 'transparent', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Always Include Options */}
                                                                                    {step.alwaysInclude && Array.isArray(step.alwaysInclude) && (
                                                                                        <div>
                                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                                                                                <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Always Include ({step.alwaysInclude.length})</label>
                                                                                                <button onClick={() => addStepOption(phase.id, stepIndex, 'alwaysInclude')} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>+ Add</button>
                                                                                            </div>
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                                {step.alwaysInclude.map((opt, optIdx) => (
                                                                                                    <div key={optIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={typeof opt?.text === 'string' ? opt.text : ''}
                                                                                                            onChange={(e) => updateStepOption(phase.id, stepIndex, optIdx, 'text', e.target.value, 'alwaysInclude')}
                                                                                                            placeholder="Text"
                                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}
                                                                                                        />
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={typeof opt?.value === 'string' ? opt.value : ''}
                                                                                                            onChange={(e) => updateStepOption(phase.id, stepIndex, optIdx, 'value', e.target.value, 'alwaysInclude')}
                                                                                                            placeholder="Value"
                                                                                                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                                                                                        />
                                                                                                        <button onClick={() => removeStepOption(phase.id, stepIndex, optIdx, 'alwaysInclude')} style={{ background: 'transparent', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* AI Generation Info */}
                                                                                    {step.optionsGeneration && (
                                                                                        <div style={{ padding: 'var(--space-2)', background: 'rgba(16, 185, 129, 0.05)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#10b981', marginBottom: 'var(--space-1)' }}>AI Generation Prompt</label>
                                                                                            {step.optionsGeneration.promptTemplate && (
                                                                                                <textarea
                                                                                                    value={step.optionsGeneration.promptTemplate}
                                                                                                    onChange={(e) => {
                                                                                                        const newFlow = { ...config.conversationFlow };
                                                                                                        newFlow[phaseFlowKey].steps[stepIndex].optionsGeneration.promptTemplate = e.target.value;
                                                                                                        setConfig({ ...config, conversationFlow: newFlow });
                                                                                                    }}
                                                                                                    style={{ width: '100%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', minHeight: '60px', fontFamily: 'monospace' }}
                                                                                                />
                                                                                            )}
                                                                                            {/* Conditional Generation Prompts */}
                                                                                            {step.optionsGeneration.generationPrompt && typeof step.optionsGeneration.generationPrompt === 'object' && (
                                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                                                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Conditional prompts based on previous selection:</span>
                                                                                                    {Object.entries(step.optionsGeneration.generationPrompt).map(([conditionKey, promptText]) => (
                                                                                                        <div key={conditionKey} style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', overflow: 'hidden' }}>
                                                                                                            <div style={{ padding: 'var(--space-1) var(--space-2)', background: 'rgba(124, 58, 237, 0.1)', borderBottom: '1px solid var(--color-border)' }}>
                                                                                                                <code style={{ fontSize: '10px', color: 'var(--color-primary)', fontWeight: 'bold' }}>{conditionKey}</code>
                                                                                                            </div>
                                                                                                            <textarea
                                                                                                                value={promptText}
                                                                                                                onChange={(e) => {
                                                                                                                    const newFlow = { ...config.conversationFlow };
                                                                                                                    newFlow[phaseFlowKey].steps[stepIndex].optionsGeneration.generationPrompt[conditionKey] = e.target.value;
                                                                                                                    setConfig({ ...config, conversationFlow: newFlow });
                                                                                                                }}
                                                                                                                style={{ width: '100%', background: 'transparent', border: 'none', padding: 'var(--space-2)', fontSize: '10px', color: 'var(--color-text-secondary)', minHeight: '50px', fontFamily: 'inherit', resize: 'vertical' }}
                                                                                                            />
                                                                                                        </div>
                                                                                                    ))}
                                                                                                    {step.optionsGeneration.basedOn && (
                                                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', marginTop: 'var(--space-1)' }}>
                                                                                                            <span style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>Context:</span>
                                                                                                            {step.optionsGeneration.basedOn.map((varName, vIdx) => (
                                                                                                                <code key={vIdx} style={{ background: 'var(--color-surface)', padding: '1px 4px', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: '10px', color: 'var(--color-primary)' }}>{varName}</code>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Casting Generation Prompt - for actors step */}
                                                                                    {step.field === 'videoSettings.actors' && (
                                                                                        <div style={{ background: 'rgba(236, 72, 153, 0.05)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(236, 72, 153, 0.2)' }}>
                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: '#ec4899' }}>🎭 Casting Generation Prompt</span>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', background: 'rgba(124, 58, 237, 0.1)', color: 'var(--color-primary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>from code</span>
                                                                                            </div>
                                                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                                                Generates 4 distinct main character options based on product, target audience, marketing angle, visual style, tone, and desired emotion.
                                                                                            </div>
                                                                                            <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                                                {['product', 'targetAudience', 'marketingAngle', 'visualStyle', 'tone', 'desiredEmotion'].map(v => (
                                                                                                    <code key={v} style={{ background: 'var(--color-surface)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: '10px', color: 'var(--color-primary)' }}>{v}</code>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Location Scouting Prompt - for scenery step */}
                                                                                    {step.field === 'videoSettings.scenery' && (
                                                                                        <div style={{ background: 'rgba(16, 185, 129, 0.05)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: '#10b981' }}>📍 Location Scouting Prompt</span>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', background: 'rgba(124, 58, 237, 0.1)', color: 'var(--color-primary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>from code</span>
                                                                                            </div>
                                                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                                                Generates 4 distinct location options. Also triggers Combined Scene and Product Scene generation after selection.
                                                                                            </div>
                                                                                            <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                                                {['product', 'targetAudience', 'marketingAngle', 'visualStyle', 'videoStyle', 'visualConcept', 'desiredEmotion'].map(v => (
                                                                                                    <code key={v} style={{ background: 'var(--color-surface)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: '10px', color: 'var(--color-primary)' }}>{v}</code>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* First Frame Generation Prompt */}
                                                                                    {step.field === 'videoSettings.firstFrame' && (
                                                                                        <div style={{ background: 'rgba(245, 158, 11, 0.05)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: 'var(--color-warning)' }}>🎬 First Frame Generation</span>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', background: 'rgba(124, 58, 237, 0.1)', color: 'var(--color-primary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>from code</span>
                                                                                            </div>
                                                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                                                Generates 4 distinct opening frame options based on script and creative direction.
                                                                                            </div>
                                                                                            <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                                                {['script', 'product', 'videoStyle', 'visualConcept', 'targetAudience', 'desiredEmotion', 'selectedCasting', 'selectedLocation'].map(v => (
                                                                                                    <code key={v} style={{ background: 'var(--color-surface)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: '10px', color: 'var(--color-primary)' }}>{v}</code>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Last Frame Generation Prompt */}
                                                                                    {step.field === 'videoSettings.lastFrame' && (
                                                                                        <div style={{ background: 'rgba(245, 158, 11, 0.05)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'bold', color: 'var(--color-warning)' }}>🎬 Last Frame Generation</span>
                                                                                                <span style={{ fontSize: 'var(--font-size-xs)', background: 'rgba(124, 58, 237, 0.1)', color: 'var(--color-primary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>from code</span>
                                                                                            </div>
                                                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                                                Generates 4 closing frame options maintaining visual continuity with first frame. Includes text overlays.
                                                                                            </div>
                                                                                            <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                                                                                                {['script', 'videoStyle', 'selectedFirstFrame', 'headline', 'cta', 'brandName', 'brandColors'].map(v => (
                                                                                                    <code key={v} style={{ background: 'var(--color-surface)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: '10px', color: 'var(--color-primary)' }}>{v}</code>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Delete Step */}
                                                                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                                        <button
                                                                                            onClick={() => removeStep(phase.id, stepIndex)}
                                                                                            style={{ background: 'transparent', border: '1px solid var(--color-error)', color: 'var(--color-error)', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                                                                                        >
                                                                                            <Trash2 size={12} /> Remove Step
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>

                                                            {/* Add Step Button */}
                                                            <button
                                                                onClick={() => addStep(phase.id)}
                                                                style={{
                                                                    marginTop: 'var(--space-3)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: 'var(--space-2)',
                                                                    padding: 'var(--space-2)',
                                                                    background: 'transparent',
                                                                    border: '1px dashed var(--color-border)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    color: 'var(--color-text-secondary)',
                                                                    cursor: 'pointer',
                                                                    width: '100%',
                                                                    fontSize: 'var(--font-size-sm)'
                                                                }}
                                                            >
                                                                <Plus size={16} /> Add Step
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Skip Message */}
                                                    {phaseFlow?.skipMessage && (
                                                        <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'rgba(245, 158, 11, 0.1)', borderRadius: 'var(--radius-md)', border: '1px dashed rgba(245, 158, 11, 0.3)' }}>
                                                            <label style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: '#f59e0b', marginBottom: 'var(--space-1)' }}>Skip Message (when phase is skipped)</label>
                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                {phaseFlow.skipMessage.isAIGenerated ? 'AI Generated' : 'Static message'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Connector to next phase */}
                                            {phaseIndex < Object.entries(config.phases).length - 1 && (
                                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-2)' }}>
                                                    <div style={{ width: 2, height: 20, background: 'var(--color-border)' }} />
                                                </div>
                                            )}

                                            {/* Creative Brief Generated Milestone - shown after brief_review phase */}
                                            {phase.id === 'brief_review' && (
                                                <>
                                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-2)' }}>
                                                        <div style={{ width: 2, height: 20, background: 'var(--color-border)' }} />
                                                    </div>
                                                    <div style={{
                                                        background: 'linear-gradient(135deg, #10b981, #059669)',
                                                        borderRadius: 'var(--radius-lg)',
                                                        padding: 'var(--space-4)',
                                                        color: 'white',
                                                        marginTop: 'var(--space-2)',
                                                        border: '3px solid #059669',
                                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                            <div style={{
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: '50%',
                                                                background: 'rgba(255,255,255,0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 'var(--font-size-xl)'
                                                            }}>
                                                                📄
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 'bold', fontSize: 'var(--font-size-md)' }}>Creative Brief Generated</div>
                                                                <div style={{ opacity: 0.9, fontSize: 'var(--font-size-sm)' }}>
                                                                    All inputs from previous phases compiled into final brief
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            marginTop: 'var(--space-3)',
                                                            padding: 'var(--space-2)',
                                                            background: 'rgba(255,255,255,0.1)',
                                                            borderRadius: 'var(--radius-md)',
                                                            fontSize: 'var(--font-size-xs)'
                                                        }}>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                                                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>Product Info</span>
                                                                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>Target Audience</span>
                                                                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>Marketing Angle</span>
                                                                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>Copy & Headlines</span>
                                                                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>Visual Style</span>
                                                                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>Assets</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-2)' }}>
                                                        <div style={{ width: 2, height: 20, background: 'var(--color-border)' }} />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Creative Brief Schema */}
                            {config.creativeBriefSchema && (
                                <div style={{ marginBottom: 'var(--space-8)' }}>
                                    <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>📝 Creative Brief Schema</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-3)' }}>
                                        {Object.entries(config.creativeBriefSchema).map(([fieldKey, field]) => (
                                            <div key={fieldKey} style={{
                                                background: 'var(--color-surface)',
                                                borderRadius: 'var(--radius-md)',
                                                padding: 'var(--space-3)',
                                                border: '1px solid var(--color-border)'
                                            }}>
                                                <code style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-primary)',
                                                    fontWeight: 600
                                                }}>{fieldKey}</code>
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                                                    Type: {typeof field?.type === 'string' ? field.type : 'object'}
                                                </div>
                                                {field?.description && typeof field.description === 'string' && (
                                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-1)' }}>
                                                        {field.description}
                                                    </div>
                                                )}
                                                {field?.enum && Array.isArray(field.enum) && (
                                                    <div style={{ marginTop: 'var(--space-1)', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                        {field.enum.map((e, i) => (
                                                            <span key={i} style={{
                                                                fontSize: '10px',
                                                                background: 'var(--color-bg-primary)',
                                                                padding: '2px 4px',
                                                                borderRadius: 'var(--radius-sm)',
                                                                color: 'var(--color-text-tertiary)'
                                                            }}>{typeof e === 'string' ? e : JSON.stringify(e)}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {field?.properties && (
                                                    <div style={{ marginTop: 'var(--space-1)', fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
                                                        Properties: {Object.keys(field.properties).join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* AI Agent Prompts */}
                            {config.aiAgentPrompts && (
                                <div style={{ marginBottom: 'var(--space-8)' }}>
                                    <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>🤖 AI Agent Prompts</h3>

                                    {config.aiAgentPrompts.systemPromptBase && (
                                        <div style={{
                                            background: 'var(--color-surface)',
                                            borderRadius: 'var(--radius-lg)',
                                            padding: 'var(--space-4)',
                                            border: '1px solid var(--color-border)',
                                            marginBottom: 'var(--space-4)'
                                        }}>
                                            <h4 style={{ margin: 0, marginBottom: 'var(--space-2)', color: '#10b981' }}>System Prompt Base</h4>
                                            <pre style={{
                                                margin: 0,
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-secondary)',
                                                whiteSpace: 'pre-wrap',
                                                maxHeight: 150,
                                                overflow: 'auto'
                                            }}>
                                                {config.aiAgentPrompts.systemPromptBase.content?.substring(0, 500)}...
                                            </pre>
                                        </div>
                                    )}

                                    {config.aiAgentPrompts.phasePrompts && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-3)' }}>
                                            {Object.entries(config.aiAgentPrompts.phasePrompts).map(([phaseId, prompt]) => (
                                                <div key={phaseId} style={{
                                                    background: 'var(--color-surface)',
                                                    borderRadius: 'var(--radius-md)',
                                                    padding: 'var(--space-3)',
                                                    border: '1px solid var(--color-border)'
                                                }}>
                                                    <h4 style={{ margin: 0, marginBottom: 'var(--space-2)', color: 'var(--color-primary)', textTransform: 'capitalize', fontSize: 'var(--font-size-sm)' }}>
                                                        {phaseId.replace(/_/g, ' ')}
                                                    </h4>
                                                    <pre style={{
                                                        margin: 0,
                                                        fontSize: '10px',
                                                        color: 'var(--color-text-tertiary)',
                                                        whiteSpace: 'pre-wrap',
                                                        maxHeight: 100,
                                                        overflow: 'auto'
                                                    }}>
                                                        {prompt.template?.substring(0, 200)}...
                                                    </pre>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Phase Transition Logic */}
                            {config.phaseTransitionLogic && (
                                <div style={{ marginBottom: 'var(--space-8)' }}>
                                    <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>🔀 Phase Transition Logic</h3>
                                    <div style={{
                                        background: 'var(--color-surface)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        border: '1px solid var(--color-border)'
                                    }}>
                                        <pre style={{
                                            margin: 0,
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {JSON.stringify(config.phaseTransitionLogic, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}

                            {/* Rabbit Hole Detection */}
                            {config.rabbitHoleDetection && (
                                <div>
                                    <h3 style={{ margin: 0, marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)' }}>🕳️ Rabbit Hole Detection</h3>
                                    <div style={{
                                        background: 'var(--color-surface)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        border: '1px solid var(--color-border)'
                                    }}>
                                        <pre style={{
                                            margin: 0,
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {JSON.stringify(config.rabbitHoleDetection, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'flowchart' && (
                    <FlowchartTab config={config} />
                )}

                {activeTab === 'prompts' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                        <div style={{
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-xl)',
                            padding: 'var(--space-6)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <h3 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'bold', marginBottom: 'var(--space-4)', margin: 0 }}>System Prompts</h3>

                            {/* System Prompt Base */}
                            <div style={{ marginBottom: 'var(--space-8)' }}>
                                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>Base System Prompt (Applied to all phases)</label>
                                <textarea
                                    value={config.aiAgentPrompts?.systemPromptBase?.content || ''}
                                    onChange={(e) => {
                                        setConfig(prev => ({
                                            ...prev,
                                            aiAgentPrompts: {
                                                ...prev.aiAgentPrompts,
                                                systemPromptBase: {
                                                    ...prev.aiAgentPrompts.systemPromptBase,
                                                    content: e.target.value
                                                }
                                            }
                                        }));
                                    }}
                                    style={{
                                        width: '100%',
                                        background: 'var(--color-bg-primary)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontFamily: 'monospace',
                                        color: 'var(--color-text-primary)',
                                        outline: 'none',
                                        minHeight: '300px'
                                    }}
                                />
                            </div>

                            {/* Phase Specific Prompts */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                                <h4 style={{ fontWeight: 600, color: 'var(--color-text-secondary)', margin: 0 }}>Phase Specific Prompts</h4>
                                {Object.entries(config.aiAgentPrompts?.phasePrompts || {}).map(([phaseId, promptConfig]) => (
                                    <div key={phaseId} style={{
                                        background: 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        border: '1px solid var(--color-border)'
                                    }}>
                                        <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-primary)', marginBottom: 'var(--space-2)', textTransform: 'capitalize' }}>{phaseId.replace('_', ' ')} Prompt</label>
                                        <textarea
                                            value={promptConfig.template || ''}
                                            onChange={(e) => {
                                                setConfig(prev => ({
                                                    ...prev,
                                                    aiAgentPrompts: {
                                                        ...prev.aiAgentPrompts,
                                                        phasePrompts: {
                                                            ...prev.aiAgentPrompts.phasePrompts,
                                                            [phaseId]: {
                                                                ...prev.aiAgentPrompts.phasePrompts[phaseId],
                                                                template: e.target.value
                                                            }
                                                        }
                                                    }
                                                }));
                                            }}
                                            style={{
                                                width: '100%',
                                                background: 'var(--color-surface)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                padding: 'var(--space-3)',
                                                fontSize: 'var(--font-size-sm)',
                                                fontFamily: 'monospace',
                                                color: 'var(--color-text-primary)',
                                                outline: 'none',
                                                minHeight: '200px'
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'raw' && (
                    <div style={{
                        background: 'var(--color-surface)',
                        borderRadius: 'var(--radius-xl)',
                        padding: 'var(--space-6)',
                        border: '1px solid var(--color-border)',
                        height: 'calc(100vh - 200px)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'bold', margin: 0 }}>Raw JSON Configuration</h3>
                            {jsonError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}><AlertCircle size={16} /> {jsonError}</span>}
                        </div>
                        <textarea
                            defaultValue={JSON.stringify(config, null, 2)}
                            onChange={handleJsonChange}
                            style={{
                                width: '100%',
                                height: '100%',
                                background: 'var(--color-bg-primary)',
                                border: jsonError ? '1px solid var(--color-error)' : '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-lg)',
                                padding: 'var(--space-4)',
                                fontFamily: 'monospace',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-primary)',
                                outline: 'none'
                            }}
                        />
                    </div>
                )}
            </div>
        </div >
    );
}
